import React, { useState, useEffect } from 'react';

// ============================================================================
// SCHEMA MARKUP TAB
// ============================================================================
const SchemaMarkupTab = ({ isDarkMode, getAuthToken, showNotification, metaTags, loadSEOData }) => {
  const [generating, setGenerating] = useState(null);
  const [preview, setPreview] = useState(null);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const ALL_PAGES = ['/', '/translation', '/dictionary', '/games', '/my-daily-words', '/membership', '/amazigh-translation', '/travel-tools', '/MoroccanHolidays', '/decoder', '/about', '/help', '/TermsOfUse', '/PrivacyPolicy'];

  const getSchemaForPage = (pagePath) => {
    const meta = metaTags.find(m => m.page_path === pagePath);
    return meta?.schema_markup || null;
  };

  const handleGenerate = async (pagePath) => {
    setGenerating(pagePath);
    try {
      const res = await fetch(`${backendUrl}/api/admin/seo/generate-schema`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getAuthToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ page_path: pagePath })
      });
      if (res.ok) {
        const data = await res.json();
        setPreview({ pagePath, schema: data.schema });
      }
    } catch (err) { showNotification('Failed to generate schema', 'error'); }
    setGenerating(null);
  };

  const handleApply = async () => {
    if (!preview) return;
    try {
      const res = await fetch(`${backendUrl}/api/admin/seo/fix/generate_schema`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getAuthToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ page_path: preview.pagePath })
      });
      if (res.ok) {
        showNotification(`Schema applied for ${preview.pagePath}`, 'success');
        setPreview(null);
        await loadSEOData();
      }
    } catch (err) { showNotification('Failed to apply schema', 'error'); }
  };

  const handleGenerateAll = async () => {
    setGenerating('all');
    let count = 0;
    for (const pagePath of ALL_PAGES) {
      if (!getSchemaForPage(pagePath)) {
        try {
          await fetch(`${backendUrl}/api/admin/seo/fix/generate_schema`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${getAuthToken()}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ page_path: pagePath })
          });
          count++;
        } catch (e) { /* continue */ }
      }
    }
    showNotification(`Generated schema for ${count} pages`, 'success');
    await loadSEOData();
    setGenerating(null);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Schema Markup (JSON-LD)</h2>
          <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Structured data helps Google show rich results for your pages</p>
        </div>
        <button onClick={handleGenerateAll} disabled={generating !== null} className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 text-sm font-medium">
          {generating === 'all' ? 'Generating...' : 'Generate All Missing'}
        </button>
      </div>

      <div className="space-y-3">
        {ALL_PAGES.map(pagePath => {
          const schema = getSchemaForPage(pagePath);
          const hasSchema = !!schema;
          let schemaType = '';
          try { if (schema) { const parsed = JSON.parse(schema); schemaType = parsed['@type'] || ''; } } catch (e) { /* ignore */ }

          return (
            <div key={pagePath} className={`p-4 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className={`text-lg ${hasSchema ? 'text-green-500' : 'text-red-500'}`}>{hasSchema ? '\u2705' : '\u274c'}</span>
                  <div>
                    <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{pagePath}</span>
                    {schemaType && <span className={`ml-2 text-xs px-2 py-0.5 rounded ${isDarkMode ? 'bg-purple-900 text-purple-300' : 'bg-purple-100 text-purple-700'}`}>{schemaType}</span>}
                  </div>
                </div>
                <div className="flex gap-2">
                  {hasSchema && (
                    <button onClick={() => setPreview({ pagePath, schema })} className={`px-3 py-1 rounded text-sm ${isDarkMode ? 'bg-gray-600 text-gray-200' : 'bg-gray-100 text-gray-700'}`}>View</button>
                  )}
                  <button onClick={() => handleGenerate(pagePath)} disabled={generating !== null} className="px-3 py-1 bg-purple-600 text-white rounded text-sm hover:bg-purple-700 disabled:opacity-50">
                    {generating === pagePath ? '...' : hasSchema ? 'Regenerate' : 'Generate'}
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Preview Modal */}
      {preview && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={`max-w-2xl w-full max-h-[80vh] overflow-auto rounded-xl p-6 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <h3 className={`text-lg font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Schema for {preview.pagePath}</h3>
            <pre className={`p-4 rounded-lg text-sm overflow-auto mb-4 ${isDarkMode ? 'bg-gray-900 text-green-400' : 'bg-gray-100 text-gray-800'}`} style={{maxHeight: '400px'}}>
              {typeof preview.schema === 'string' ? preview.schema : JSON.stringify(JSON.parse(preview.schema), null, 2)}
            </pre>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setPreview(null)} className={`px-4 py-2 rounded-lg ${isDarkMode ? 'bg-gray-700 text-white' : 'bg-gray-200 text-gray-800'}`}>Close</button>
              <button onClick={handleApply} className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700">Apply</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// SEO TOOLS TAB — Audit, Bulk Optimize, Keyword Research, Recommendations
// ============================================================================
const SEOToolsTab = ({ isDarkMode, getAuthToken, showNotification, metaTags, loadSEOData }) => {
  const [auditData, setAuditData] = useState(null);
  const [nicheKeywords, setNicheKeywords] = useState(null);
  const [bulkPreview, setBulkPreview] = useState(null);
  const [loading, setLoading] = useState('');
  const [fixing, setFixing] = useState(null);
  const [keywordFilter, setKeywordFilter] = useState('all');
  const [difficultyFilter, setDifficultyFilter] = useState('all');
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const runAudit = async () => {
    setLoading('audit');
    try {
      const res = await fetch(`${backendUrl}/api/admin/seo/audit`, {
        headers: { 'Authorization': `Bearer ${getAuthToken()}` }
      });
      if (res.ok) setAuditData(await res.json());
    } catch (err) { showNotification('Audit failed', 'error'); }
    setLoading('');
  };

  const loadKeywords = async () => {
    setLoading('keywords');
    try {
      const res = await fetch(`${backendUrl}/api/admin/seo/niche-keywords`, {
        headers: { 'Authorization': `Bearer ${getAuthToken()}` }
      });
      if (res.ok) {
        const data = await res.json();
        setNicheKeywords(data.keywords);
      }
    } catch (err) { showNotification('Failed to load keywords', 'error'); }
    setLoading('');
  };

  const handleBulkGenerate = async (forceRegenerate = false) => {
    setLoading('bulk');
    try {
      const res = await fetch(`${backendUrl}/api/admin/seo/bulk-generate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getAuthToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ force_regenerate: forceRegenerate })
      });
      if (res.ok) {
        const data = await res.json();
        setBulkPreview(data.generated);
        if (data.generated.length === 0 && !forceRegenerate) {
          showNotification('All pages already have meta tags — nothing missing!', 'success');
        }
      }
    } catch (err) { showNotification('Bulk generate failed', 'error'); }
    setLoading('');
  };

  const handleApplyBulk = async () => {
    setLoading('applying');
    try {
      const res = await fetch(`${backendUrl}/api/admin/seo/apply-generated`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getAuthToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ meta_tags: bulkPreview })
      });
      if (res.ok) {
        const data = await res.json();
        showNotification(`Applied ${data.applied} meta tags, skipped ${data.skipped} manual`, 'success');
        setBulkPreview(null);
        await loadSEOData();
      }
    } catch (err) { showNotification('Failed to apply', 'error'); }
    setLoading('');
  };

  const handleFix = async (issue) => {
    if (issue.fix_action === 'none') return;
    setFixing(issue.description);
    try {
      const res = await fetch(`${backendUrl}/api/admin/seo/fix/${issue.fix_action}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getAuthToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(issue.fix_data || {})
      });
      if (res.ok) { await runAudit(); await loadSEOData(); }
    } catch (err) { showNotification('Fix failed', 'error'); }
    setFixing(null);
  };

  const handleFixAll = async (severity) => {
    setFixing('all');
    try {
      const res = await fetch(`${backendUrl}/api/admin/seo/fix-all`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getAuthToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ severity })
      });
      if (res.ok) {
        const data = await res.json();
        showNotification(`Fixed ${data.fixed} issues`, 'success');
        await runAudit();
        await loadSEOData();
      }
    } catch (err) { showNotification('Fix all failed', 'error'); }
    setFixing(null);
  };

  const sevIcon = (s) => s === 'critical' ? '\ud83d\udd34' : s === 'warning' ? '\ud83d\udfe1' : '\ud83d\udfe2';
  const sevBg = (s, dark) => s === 'critical' ? (dark ? 'bg-red-900/30' : 'bg-red-50') : s === 'warning' ? (dark ? 'bg-yellow-900/30' : 'bg-yellow-50') : (dark ? 'bg-blue-900/30' : 'bg-blue-50');
  const diffColor = (d) => d === 'easy' ? 'text-green-500' : d === 'medium' ? 'text-yellow-500' : 'text-red-500';
  const diffBg = (d, dark) => d === 'easy' ? (dark ? 'bg-green-900/30' : 'bg-green-50') : d === 'medium' ? (dark ? 'bg-yellow-900/30' : 'bg-yellow-50') : (dark ? 'bg-red-900/30' : 'bg-red-50');

  const filteredKeywords = nicheKeywords?.filter(k => {
    if (keywordFilter !== 'all' && k.category !== keywordFilter) return false;
    if (difficultyFilter !== 'all' && k.difficulty !== difficultyFilter) return false;
    return true;
  });

  const categories = nicheKeywords ? [...new Set(nicheKeywords.map(k => k.category))] : [];

  return (
    <div className="space-y-8">
      <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>SEO Tools</h2>

      {/* Section A: Website Audit */}
      <div className={`p-6 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Full Website Audit</h3>
          <button onClick={runAudit} disabled={loading === 'audit'} className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 text-sm font-medium">
            {loading === 'audit' ? 'Scanning...' : auditData ? 'Re-run Audit' : 'Run Full Audit'}
          </button>
        </div>

        {auditData && (
          <div>
            {/* Score + Summary */}
            <div className="flex items-center gap-6 mb-4">
              <div className={`text-4xl font-bold ${auditData.overall_score >= 80 ? 'text-green-500' : auditData.overall_score >= 60 ? 'text-yellow-500' : 'text-red-500'}`}>
                {auditData.overall_score}%
              </div>
              <div className="flex gap-4">
                <span className="text-red-500 font-medium">{auditData.summary.critical} Critical</span>
                <span className="text-yellow-500 font-medium">{auditData.summary.warning} Warnings</span>
                <span className="text-green-500 font-medium">{auditData.summary.passed} Passed</span>
              </div>
            </div>

            {/* Fix All Buttons */}
            {(auditData.summary.critical > 0 || auditData.summary.warning > 0) && (
              <div className="flex gap-3 mb-4">
                {auditData.summary.critical > 0 && (
                  <button onClick={() => handleFixAll('critical')} disabled={fixing} className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium disabled:opacity-50">
                    {fixing === 'all' ? 'Fixing...' : `Fix All Critical (${auditData.summary.critical})`}
                  </button>
                )}
                {auditData.summary.warning > 0 && (
                  <button onClick={() => handleFixAll('warning')} disabled={fixing} className="px-4 py-2 bg-yellow-600 text-white rounded-lg text-sm font-medium disabled:opacity-50">
                    Fix All Warnings ({auditData.summary.warning})
                  </button>
                )}
              </div>
            )}

            {/* Issues List */}
            <div className="space-y-2 max-h-[400px] overflow-auto">
              {auditData.issues.map((issue, idx) => (
                <div key={idx} className={`p-3 rounded-lg flex items-start justify-between gap-3 ${sevBg(issue.severity, isDarkMode)}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span>{sevIcon(issue.severity)}</span>
                      <span className={`font-medium text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{issue.description}</span>
                    </div>
                    <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{issue.impact}</p>
                  </div>
                  {issue.fix_action !== 'none' && (
                    <button onClick={() => handleFix(issue)} disabled={fixing} className="px-3 py-1 bg-purple-600 text-white rounded text-xs font-medium whitespace-nowrap disabled:opacity-50">
                      {fixing === issue.description ? '...' : 'Fix'}
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Section B: Bulk Page Optimizer */}
      <div className={`p-6 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}>
        <h3 className={`text-lg font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Bulk Page Optimizer</h3>
        <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          Generate optimized meta tags for all pages at once. Pages you manually edited are protected and will be skipped.
        </p>
        <div className="flex gap-3">
          <button onClick={() => handleBulkGenerate(false)} disabled={loading === 'bulk'} className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 text-sm font-medium">
            {loading === 'bulk' ? 'Generating...' : 'Generate Missing Pages'}
          </button>
          <button onClick={() => handleBulkGenerate(true)} disabled={loading === 'bulk'} className={`px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50 ${isDarkMode ? 'bg-gray-600 text-gray-200 hover:bg-gray-500' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}>
            {loading === 'bulk' ? 'Generating...' : 'Regenerate All (non-manual)'}
          </button>
        </div>

        {/* Bulk Preview */}
        {bulkPreview && bulkPreview.length > 0 && (
          <div className="mt-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Preview ({bulkPreview.length} pages)</h4>
              <div className="flex gap-2">
                <button onClick={() => setBulkPreview(null)} className={`px-3 py-1 rounded text-sm ${isDarkMode ? 'bg-gray-600 text-white' : 'bg-gray-200 text-gray-800'}`}>Cancel</button>
                <button onClick={handleApplyBulk} disabled={loading === 'applying'} className="px-3 py-1 bg-green-600 text-white rounded text-sm font-medium disabled:opacity-50">
                  {loading === 'applying' ? 'Applying...' : 'Apply All'}
                </button>
              </div>
            </div>
            <div className="space-y-2 max-h-[400px] overflow-auto">
              {bulkPreview.map((meta, idx) => (
                <div key={idx} className={`p-3 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`font-medium text-sm ${isDarkMode ? 'text-purple-300' : 'text-purple-700'}`}>{meta.page_path}</span>
                    {meta.is_update && <span className="text-xs px-1.5 py-0.5 rounded bg-yellow-200 text-yellow-800">Update</span>}
                  </div>
                  <p className={`text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{meta.meta_title} <span className={`text-xs ${meta.meta_title.length >= 30 && meta.meta_title.length <= 60 ? 'text-green-500' : 'text-red-500'}`}>({meta.meta_title.length}ch)</span></p>
                  <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{meta.meta_description} <span className={meta.meta_description.length >= 120 && meta.meta_description.length <= 160 ? 'text-green-500' : 'text-red-500'}>({meta.meta_description.length}ch)</span></p>
                </div>
              ))}
            </div>
          </div>
        )}
        {bulkPreview && bulkPreview.length === 0 && (
          <p className={`mt-4 text-sm ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>All pages already have meta tags. Nothing to generate.</p>
        )}
      </div>

      {/* Section C: Niche Keyword Research */}
      <div className={`p-6 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Niche Keyword Research</h3>
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>80+ pre-researched keywords for Darija/Moroccan Arabic learning</p>
          </div>
          {!nicheKeywords && (
            <button onClick={loadKeywords} disabled={loading === 'keywords'} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-medium">
              {loading === 'keywords' ? 'Loading...' : 'Load Keywords'}
            </button>
          )}
        </div>

        {nicheKeywords && (
          <div>
            {/* Filters */}
            <div className="flex flex-wrap gap-2 mb-4">
              <select value={keywordFilter} onChange={(e) => setKeywordFilter(e.target.value)} className={`px-3 py-1 rounded-lg text-sm border ${isDarkMode ? 'bg-gray-800 text-white border-gray-600' : 'bg-white text-gray-900 border-gray-300'}`}>
                <option value="all">All Categories</option>
                {categories.map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
              </select>
              <select value={difficultyFilter} onChange={(e) => setDifficultyFilter(e.target.value)} className={`px-3 py-1 rounded-lg text-sm border ${isDarkMode ? 'bg-gray-800 text-white border-gray-600' : 'bg-white text-gray-900 border-gray-300'}`}>
                <option value="all">All Difficulty</option>
                <option value="easy">Easy</option>
                <option value="medium">Medium</option>
                <option value="hard">Hard</option>
              </select>
              <span className={`text-sm py-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Showing {filteredKeywords?.length || 0} keywords
              </span>
            </div>

            {/* Keywords Table */}
            <div className="overflow-auto max-h-[500px]">
              <table className="w-full text-sm">
                <thead className={`sticky top-0 ${isDarkMode ? 'bg-gray-700' : 'bg-white'}`}>
                  <tr className={`border-b ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                    <th className={`text-left py-2 px-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Keyword</th>
                    <th className={`text-left py-2 px-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Category</th>
                    <th className={`text-left py-2 px-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Difficulty</th>
                    <th className={`text-left py-2 px-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Intent</th>
                    <th className={`text-left py-2 px-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Target Page</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredKeywords?.map((kw, idx) => (
                    <tr key={idx} className={`border-b ${isDarkMode ? 'border-gray-600' : 'border-gray-100'}`}>
                      <td className={`py-2 px-3 font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{kw.keyword}</td>
                      <td className={`py-2 px-3 capitalize ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{kw.category}</td>
                      <td className="py-2 px-3">
                        <span className={`px-2 py-0.5 rounded text-xs font-bold ${diffBg(kw.difficulty, isDarkMode)} ${diffColor(kw.difficulty)}`}>
                          {kw.difficulty}
                        </span>
                      </td>
                      <td className={`py-2 px-3 capitalize ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{kw.intent}</td>
                      <td className={`py-2 px-3 ${isDarkMode ? 'text-purple-300' : 'text-purple-600'}`}>{kw.target_page}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Section D: Recommendations */}
      <div className={`p-6 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}>
        <h3 className={`text-lg font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Recommendations to Rank Higher</h3>
        <div className="space-y-3">
          {[
            { priority: 1, title: 'Submit sitemap to Google Search Console', desc: 'Google needs your sitemap to discover all pages. Go to search.google.com/search-console', impact: 'critical' },
            { priority: 2, title: 'Add structured data to all pages', desc: 'Schema markup enables rich results in Google (stars, FAQ, breadcrumbs)', impact: 'critical' },
            { priority: 3, title: 'Configure Google Analytics', desc: 'Track visitor behavior, traffic sources, and conversion to understand what works', impact: 'warning' },
            { priority: 4, title: 'Create content targeting long-tail keywords', desc: 'Blog posts about "how to say X in Darija" can rank easily with low competition', impact: 'warning' },
            { priority: 5, title: 'Get backlinks from language learning directories', desc: 'Submit to education directories, language learning forums, and Morocco travel sites', impact: 'warning' },
            { priority: 6, title: 'Add social media profiles and link them', desc: 'Social profiles boost trust signals and provide additional discovery channels', impact: 'info' },
          ].map((rec, idx) => (
            <div key={idx} className={`p-4 rounded-lg flex items-start gap-3 ${
              rec.impact === 'critical' ? (isDarkMode ? 'bg-red-900/20' : 'bg-red-50') :
              rec.impact === 'warning' ? (isDarkMode ? 'bg-yellow-900/20' : 'bg-yellow-50') :
              (isDarkMode ? 'bg-blue-900/20' : 'bg-blue-50')
            }`}>
              <span className={`text-lg font-bold ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>#{rec.priority}</span>
              <div>
                <h4 className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{rec.title}</h4>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{rec.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// API PROVIDERS TAB — Configure AI provider for future use
// ============================================================================
const DEFAULT_SEO_PROMPT = `You are a senior SEO consultant specialized in language-learning websites in the Moroccan Darija / Arabic dialect niche.

Analyze the provided site data and return ONLY valid JSON with: overall_score, score_breakdown (technical/content/competitive), issues with concrete suggested fixes, competitors with their keywords, keyword_opportunities, and actions_to_improve_score.

Title: 30-60 chars. Description: 120-160 chars. Always provide concrete example text. Match search intent for Darija learners.`;

const APIProvidersTab = ({ isDarkMode, getAuthToken, showNotification }) => {
  const [config, setConfig] = useState({
    provider: 'none', api_key: '', model: '', api_key_set: false, api_key_masked: '',
    temperature: 0.3, system_prompt: '', max_tokens: 4000,
  });
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [showKey, setShowKey] = useState(false);
  const [auditing, setAuditing] = useState(false);
  const [auditResult, setAuditResult] = useState(null);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${backendUrl}/api/admin/seo/api-config`, {
          headers: { 'Authorization': `Bearer ${getAuthToken()}` }
        });
        if (res.ok) {
          const data = await res.json();
          setConfig(prev => ({ ...prev, ...data, api_key: '' }));
        }
      } catch (e) { /* ignore */ }
    };
    load();
  }, []);

  const handleSave = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${backendUrl}/api/admin/seo/api-config`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${getAuthToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          provider: config.provider,
          api_key: config.api_key,
          model: config.model,
          temperature: config.temperature,
          system_prompt: config.system_prompt,
          max_tokens: config.max_tokens,
        })
      });
      if (res.ok) {
        showNotification('API configuration saved', 'success');
        // Reload to get masked key
        const reload = await fetch(`${backendUrl}/api/admin/seo/api-config`, {
          headers: { 'Authorization': `Bearer ${getAuthToken()}` }
        });
        if (reload.ok) {
          const data = await reload.json();
          setConfig(prev => ({ ...prev, ...data, api_key: '' }));
        }
      }
    } catch (e) { showNotification('Failed to save', 'error'); }
    setLoading(false);
  };

  const confirmRemoveKey = async () => {
    setShowRemoveModal(false);
    setLoading(true);
    try {
      const res = await fetch(`${backendUrl}/api/admin/seo/api-config`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${getAuthToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          provider: config.provider,
          model: config.model,
          temperature: config.temperature,
          system_prompt: config.system_prompt,
          max_tokens: config.max_tokens,
          clear_key: true,
        })
      });
      if (res.ok) {
        showNotification('API key removed', 'success');
        setConfig(prev => ({ ...prev, api_key: '', api_key_set: false, api_key_masked: '' }));
        setTestResult(null);
        setAuditResult(null);
      }
    } catch (e) { showNotification('Failed to remove key', 'error'); }
    setLoading(false);
  };

  const handleRemoveKey = () => setShowRemoveModal(true);

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await fetch(`${backendUrl}/api/admin/seo/test-api`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getAuthToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });
      if (res.ok) {
        const data = await res.json();
        setTestResult(data);
      }
    } catch (e) { setTestResult({ success: false, message: 'Connection failed' }); }
    setTesting(false);
  };

  const models = {
    none: [],
    claude: ['claude-sonnet-4-20250514', 'claude-haiku-4-5-20251001'],
    gemini: ['gemini-2.0-flash', 'gemini-2.0-flash-lite'],
    openai: ['gpt-4o-mini', 'gpt-4o'],
    groq: ['llama-3.3-70b-versatile', 'llama-3.1-8b-instant', 'mixtral-8x7b-32768'],
    deepseek: ['deepseek-chat', 'deepseek-reasoner'],
  };

  const handleAudit = async () => {
    setAuditing(true);
    setAuditResult(null);
    try {
      const res = await fetch(`${backendUrl}/api/admin/seo/ai-audit`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getAuthToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });
      if (res.ok) {
        const data = await res.json();
        setAuditResult(data);
        if (data.ai_used) {
          showNotification('SEO audit complete', 'success');
        } else {
          showNotification(data.error || data.message || 'Audit returned no AI result', 'error');
        }
      } else {
        showNotification('Audit request failed', 'error');
      }
    } catch (e) { showNotification('Audit failed: ' + e.message, 'error'); }
    setAuditing(false);
  };

  return (
    <div>
      {/* Remove Key Confirmation Modal */}
      {showRemoveModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          onClick={() => setShowRemoveModal(false)}
        >
          <div
            className={`w-full max-w-md rounded-2xl shadow-2xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex items-center justify-center mb-4">
                <div className={`w-16 h-16 rounded-full flex items-center justify-center ${isDarkMode ? 'bg-red-900/40' : 'bg-red-100'}`}>
                  <span className="text-3xl">🗑️</span>
                </div>
              </div>
              <h3 className={`text-xl font-bold text-center mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Remove API Key?
              </h3>
              <p className={`text-sm text-center mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                The saved {config.provider?.charAt(0).toUpperCase() + config.provider?.slice(1)} key will be deleted from the database.
              </p>
              <p className={`text-xs text-center mb-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                You will need to paste a new key before running any AI features.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowRemoveModal(false)}
                  className={`flex-1 px-4 py-3 rounded-lg font-medium transition-colors ${isDarkMode ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmRemoveKey}
                  className="flex-1 px-4 py-3 rounded-lg font-medium bg-red-600 text-white hover:bg-red-700 transition-colors"
                >
                  Remove Key
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <h2 className={`text-2xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>API Providers</h2>
      <div className={`p-4 rounded-lg mb-6 ${isDarkMode ? 'bg-blue-900/30' : 'bg-blue-50'}`}>
        <p className={`text-sm ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>
          All SEO tools work without an API key using smart templates. Adding an AI provider enhances meta tag generation with context-aware content. This is optional.
        </p>
      </div>

      <div className={`p-6 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}>
        {/* Provider Selection */}
        <div className="mb-6">
          <label className={`block mb-2 text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Provider</label>
          <div className="flex gap-3">
            {['none', 'claude', 'gemini', 'openai', 'groq', 'deepseek'].map(p => (
              <button
                key={p}
                onClick={() => setConfig(prev => ({ ...prev, provider: p, model: models[p]?.[0] || '' }))}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  config.provider === p
                    ? 'bg-purple-600 text-white'
                    : isDarkMode ? 'bg-gray-800 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {p === 'none' ? 'None (Templates)' : p.charAt(0).toUpperCase() + p.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {config.provider !== 'none' && (
          <>
            {/* API Key */}
            <div className="mb-6">
              <label className={`block mb-2 text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>API Key</label>
              <div className="flex gap-2">
                <input
                  type={showKey ? 'text' : 'password'}
                  value={config.api_key}
                  onChange={(e) => setConfig(prev => ({ ...prev, api_key: e.target.value }))}
                  placeholder={config.api_key_set ? `Current: ${config.api_key_masked}` : 'Enter API key...'}
                  className={`flex-1 p-3 rounded-lg border-2 ${isDarkMode ? 'bg-gray-800 text-white border-gray-600' : 'bg-white text-gray-900 border-gray-300'}`}
                />
                <button onClick={() => setShowKey(!showKey)} className={`px-3 py-2 rounded-lg ${isDarkMode ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-700'}`} title={showKey ? 'Hide' : 'Show'}>
                  {showKey ? '\ud83d\ude48' : '\ud83d\udc41\ufe0f'}
                </button>
                <button
                  onClick={handleRemoveKey}
                  disabled={loading || !config.api_key_set}
                  className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap ${isDarkMode ? 'bg-red-900/40 text-red-300 hover:bg-red-900/60' : 'bg-red-50 text-red-700 hover:bg-red-100'} disabled:opacity-40 disabled:cursor-not-allowed`}
                  title={config.api_key_set ? "Remove the saved API key and save settings" : "No key to remove"}
                >
                  {'\ud83d\uddd1\ufe0f'} Remove Key & Save
                </button>
              </div>
              <p className={`text-xs mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {config.api_key_set
                  ? '\ud83d\udd11 A key is currently saved (masked above). Type a new key to replace it, or click Remove Key & Save to clear.'
                  : 'No key saved yet. Paste your API key above and click Save Settings.'}
              </p>
            </div>

            {/* Model */}
            <div className="mb-6">
              <label className={`block mb-2 text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Model</label>
              <select
                value={config.model}
                onChange={(e) => setConfig(prev => ({ ...prev, model: e.target.value }))}
                className={`w-full p-3 rounded-lg border-2 ${isDarkMode ? 'bg-gray-800 text-white border-gray-600' : 'bg-white text-gray-900 border-gray-300'}`}
              >
                {models[config.provider]?.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>

            {/* Temperature */}
            <div className="mb-6">
              <label className={`block mb-2 text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                🌡️ Temperature: <span className="font-bold text-purple-500">{Number(config.temperature ?? 0.3).toFixed(1)}</span>
                <span className={`ml-2 text-xs font-normal ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>(0.0 = strict, 1.0 = creative — SEO needs precision, default 0.3)</span>
              </label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={config.temperature ?? 0.3}
                onChange={(e) => setConfig(prev => ({ ...prev, temperature: parseFloat(e.target.value) }))}
                className="w-full"
              />
            </div>

            {/* Max Tokens */}
            <div className="mb-6">
              <label className={`block mb-2 text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Max Output Tokens
                <span className={`ml-2 text-xs font-normal ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>(higher = longer audit, more cost — default 4000)</span>
              </label>
              <input
                type="number"
                min="500"
                max="8000"
                step="100"
                value={config.max_tokens ?? 4000}
                onChange={(e) => setConfig(prev => ({ ...prev, max_tokens: parseInt(e.target.value) || 4000 }))}
                className={`w-full p-3 rounded-lg border-2 ${isDarkMode ? 'bg-gray-800 text-white border-gray-600' : 'bg-white text-gray-900 border-gray-300'}`}
              />
            </div>

            {/* System Prompt */}
            <div className="mb-6">
              <label className={`block mb-2 text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                🎯 SEO System Prompt
                <span className={`ml-2 text-xs font-normal ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>(tells the AI exactly how to audit your site — leave empty to use the built-in default)</span>
              </label>
              <textarea
                rows={10}
                value={config.system_prompt || ''}
                onChange={(e) => setConfig(prev => ({ ...prev, system_prompt: e.target.value }))}
                placeholder={DEFAULT_SEO_PROMPT}
                className={`w-full p-3 rounded-lg border-2 font-mono text-xs ${isDarkMode ? 'bg-gray-800 text-white border-gray-600' : 'bg-white text-gray-900 border-gray-300'}`}
              />
              <button
                type="button"
                onClick={() => setConfig(prev => ({ ...prev, system_prompt: DEFAULT_SEO_PROMPT }))}
                className={`mt-2 text-xs underline ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`}
              >
                Load default prompt
              </button>
            </div>
          </>
        )}

        {/* Actions */}
        <div className="flex gap-3">
          <button onClick={handleSave} disabled={loading} className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 font-medium">
            {loading ? 'Saving...' : 'Save Settings'}
          </button>
          {config.provider !== 'none' && (
            <button onClick={handleTest} disabled={testing} className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium">
              {testing ? 'Testing...' : 'Test Connection'}
            </button>
          )}
        </div>

        {/* Test Result */}
        {testResult && (
          <div className={`mt-4 p-3 rounded-lg ${testResult.success ? (isDarkMode ? 'bg-green-900/30' : 'bg-green-50') : (isDarkMode ? 'bg-red-900/30' : 'bg-red-50')}`}>
            <span className={`text-sm font-medium ${testResult.success ? 'text-green-500' : 'text-red-500'}`}>
              {testResult.success ? '\u2705' : '\u274c'} {testResult.message}
            </span>
          </div>
        )}

        {/* Status */}
        <div className={`mt-6 p-4 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Status: {config.provider === 'none'
              ? '\ud83d\udfe2 Using smart templates (no API key needed)'
              : config.api_key_set
                ? `\ud83d\udfe2 ${config.provider.charAt(0).toUpperCase() + config.provider.slice(1)} API configured`
                : `\ud83d\udfe1 ${config.provider.charAt(0).toUpperCase() + config.provider.slice(1)} selected but no API key set`
            }
          </p>
        </div>
      </div>

      {/* AI SEO Audit */}
      {config.provider !== 'none' && config.api_key_set && (
        <div className={`mt-6 p-6 rounded-lg border-2 ${isDarkMode ? 'bg-gradient-to-br from-purple-900/30 to-pink-900/30 border-purple-700' : 'bg-gradient-to-br from-purple-50 to-pink-50 border-purple-300'}`}>
          <h3 className={`text-xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{'\ud83d\ude80'} AI SEO Audit</h3>
          <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            Run a full audit using your configured AI. Returns score breakdown, competitor insights, keyword opportunities, and ranked actions to improve your SEO.
          </p>
          <button
            onClick={handleAudit}
            disabled={auditing}
            className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 font-bold text-base shadow-lg"
          >
            {auditing ? '\u23f3 Analyzing... (10-30s)' : '\ud83d\ude80 Check My SEO'}
          </button>

          {auditResult && (
            <AuditResultsPanel result={auditResult} isDarkMode={isDarkMode} />
          )}
        </div>
      )}
      {config.provider !== 'none' && !config.api_key_set && (
        <div className={`mt-6 p-4 rounded-lg ${isDarkMode ? 'bg-yellow-900/30 text-yellow-300' : 'bg-yellow-50 text-yellow-800'}`}>
          <p className="text-sm">{'\u26a0\ufe0f'} Save a valid API key first. Then click "Check My SEO" to run the AI audit.</p>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// AUDIT RESULTS PANEL
// ============================================================================
const AuditResultsPanel = ({ result, isDarkMode }) => {
  const [appliedKeys, setAppliedKeys] = useState({});
  const [applyingKey, setApplyingKey] = useState(null);
  const [fieldOverrides, setFieldOverrides] = useState({});
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const getAuthToken = () => {
    try { return localStorage.getItem('admin_token') || ''; } catch (e) { return ''; }
  };

  const ALLOWED_FIELDS = ['meta_title', 'meta_description', 'meta_keywords', 'og_title', 'og_description', 'schema_markup'];

  const inferField = (issue) => {
    if (issue.field && ALLOWED_FIELDS.includes(issue.field)) return issue.field;
    const txt = `${issue.issue || ''} ${issue.suggested || ''}`.toLowerCase();
    if (txt.includes('schema') || txt.includes('json-ld') || txt.includes('@type')) return 'schema_markup';
    if (txt.includes('keyword')) return 'meta_keywords';
    if (txt.includes('og:title') || (txt.includes('og ') && txt.includes('title'))) return 'og_title';
    if (txt.includes('og:description') || (txt.includes('og ') && txt.includes('description'))) return 'og_description';
    if (txt.includes('description')) return 'meta_description';
    if (txt.includes('title')) return 'meta_title';
    const len = (issue.suggested || '').length;
    if (len >= 30 && len <= 70) return 'meta_title';
    if (len >= 100 && len <= 200) return 'meta_description';
    return 'meta_title';
  };

  const applyFix = async (key, page_path, field, value) => {
    setApplyingKey(key);
    try {
      const res = await fetch(`${backendUrl}/api/admin/seo/apply-fix`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getAuthToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ page_path, field, value })
      });
      const data = await res.json();
      if (data.applied) {
        setAppliedKeys(prev => ({ ...prev, [key]: 'ok' }));
      } else {
        setAppliedKeys(prev => ({ ...prev, [key]: 'err:' + (data.message || 'failed') }));
      }
    } catch (e) {
      setAppliedKeys(prev => ({ ...prev, [key]: 'err:' + e.message }));
    }
    setApplyingKey(null);
  };

  if (!result) return null;
  if (!result.ai_used) {
    return (
      <div className={`mt-6 p-4 rounded-lg ${isDarkMode ? 'bg-red-900/30 text-red-300' : 'bg-red-50 text-red-700'}`}>
        <p className="text-sm font-medium">{result.error || result.message || 'Audit could not run'}</p>
        {result.raw_response && <pre className="mt-2 text-xs whitespace-pre-wrap opacity-70">{result.raw_response}</pre>}
      </div>
    );
  }

  const scoreColor = (s) => {
    if (s == null) return isDarkMode ? 'text-gray-300' : 'text-gray-600';
    if (s >= 80) return 'text-green-500';
    if (s >= 60) return 'text-yellow-500';
    return 'text-red-500';
  };

  const sevColor = (sev) => {
    if (sev === 'critical') return isDarkMode ? 'bg-red-900/40 text-red-300 border-red-700' : 'bg-red-50 text-red-700 border-red-300';
    if (sev === 'warning') return isDarkMode ? 'bg-yellow-900/40 text-yellow-300 border-yellow-700' : 'bg-yellow-50 text-yellow-700 border-yellow-300';
    return isDarkMode ? 'bg-blue-900/40 text-blue-300 border-blue-700' : 'bg-blue-50 text-blue-700 border-blue-300';
  };

  const effortBadge = (e) => {
    const colors = {
      low: isDarkMode ? 'bg-green-900/40 text-green-300' : 'bg-green-100 text-green-700',
      medium: isDarkMode ? 'bg-yellow-900/40 text-yellow-300' : 'bg-yellow-100 text-yellow-700',
      high: isDarkMode ? 'bg-red-900/40 text-red-300' : 'bg-red-100 text-red-700',
    };
    return colors[e] || colors.medium;
  };

  return (
    <div className="mt-6 space-y-6">
      {/* Overall + Breakdown */}
      <div className={`p-6 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow`}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className={`text-xs uppercase tracking-wide ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Overall SEO Score</p>
            <p className={`text-6xl font-bold ${scoreColor(result.overall_score)}`}>{result.overall_score ?? '\u2014'}<span className="text-2xl opacity-60">/100</span></p>
          </div>
          <div className={`text-xs px-3 py-1 rounded-full ${isDarkMode ? 'bg-purple-900/40 text-purple-300' : 'bg-purple-100 text-purple-700'}`}>
            {result.provider} \u00b7 {result.model}
          </div>
        </div>
        {result.summary && (
          <p className={`text-sm italic ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{result.summary}</p>
        )}

        {result.score_breakdown && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
            {['technical', 'content', 'competitive'].map(k => {
              const b = result.score_breakdown[k];
              if (!b) return null;
              return (
                <div key={k} className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <p className={`text-xs uppercase ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{k}</p>
                  <p className={`text-3xl font-bold ${scoreColor(b.score)}`}>{b.score ?? '\u2014'}</p>
                  <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{b.explanation}</p>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Issues */}
      {Array.isArray(result.issues) && result.issues.length > 0 && (
        <div className={`p-6 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow`}>
          <h4 className={`text-lg font-bold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{'\ud83d\udd27'} Issues Found ({result.issues.length})</h4>
          <div className="space-y-3">
            {result.issues.map((issue, i) => {
              const key = `issue-${i}`;
              const applied = appliedKeys[key];
              const inferred = inferField(issue);
              const selectedField = fieldOverrides[key] || (inferred !== 'none' ? inferred : 'meta_title');
              const canApply = !!issue.suggested && !!issue.page;
              return (
                <div key={i} className={`p-4 rounded-lg border ${sevColor(issue.severity)}`}>
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="flex-1">
                      <span className="text-xs font-bold uppercase">{issue.severity}</span>
                      {issue.page && <span className={`ml-2 text-xs px-2 py-0.5 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>{issue.page}</span>}
                      <p className="font-medium mt-1">{issue.issue}</p>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      {issue.score_gain != null && <span className="text-xs font-bold">+{issue.score_gain} pts</span>}
                      {issue.effort && <span className={`text-xs px-2 py-0.5 rounded ${effortBadge(issue.effort)}`}>{issue.effort}</span>}
                    </div>
                  </div>
                  {issue.current && (
                    <p className={`text-xs mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      <span className="font-bold">Current:</span> {issue.current}
                    </p>
                  )}
                  {issue.suggested && (
                    <p className={`text-xs mt-1 ${isDarkMode ? 'text-green-300' : 'text-green-700'}`}>
                      <span className="font-bold">Suggested:</span> {issue.suggested}
                    </p>
                  )}
                  {canApply && (
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <label className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Apply to:</label>
                      <select
                        value={selectedField}
                        onChange={(e) => setFieldOverrides(prev => ({ ...prev, [key]: e.target.value }))}
                        className={`text-xs px-2 py-1 rounded border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-800'}`}
                      >
                        <option value="meta_title">Meta Title</option>
                        <option value="meta_description">Meta Description</option>
                        <option value="meta_keywords">Meta Keywords</option>
                        <option value="og_title">OG Title</option>
                        <option value="og_description">OG Description</option>
                        <option value="schema_markup">Schema Markup</option>
                      </select>
                      {applied === 'ok' ? (
                        <span className="text-xs font-bold text-green-500">{'✅'} Applied</span>
                      ) : applied && applied.startsWith('err:') ? (
                        <>
                          <span className="text-xs text-red-400">{'❌'} {applied.slice(4)}</span>
                          <button
                            onClick={() => applyFix(key, issue.page, selectedField, issue.suggested)}
                            disabled={applyingKey === key}
                            className="px-3 py-1 rounded text-xs font-medium bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50"
                          >Retry</button>
                        </>
                      ) : (
                        <button
                          onClick={() => applyFix(key, issue.page, selectedField, issue.suggested)}
                          disabled={applyingKey === key}
                          className="px-3 py-1 rounded text-xs font-medium bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50"
                        >
                          {applyingKey === key ? '⏳ Applying...' : '✨ Apply Fix'}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Competitors */}
      {Array.isArray(result.competitors) && result.competitors.length > 0 && (
        <div className={`p-6 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow`}>
          <h4 className={`text-lg font-bold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{'\ud83c\udfc6'} Competitors ({result.competitors.length})</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {result.competitors.map((c, i) => (
              <div key={i} className={`p-4 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
                <p className={`font-bold text-base ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{c.domain}</p>
                {c.what_they_do_well && (
                  <p className={`text-xs mt-2 ${isDarkMode ? 'text-green-300' : 'text-green-700'}`}>
                    <span className="font-bold">{'\u2713'} Strength:</span> {c.what_they_do_well}
                  </p>
                )}
                {c.weakness_to_exploit && (
                  <p className={`text-xs mt-1 ${isDarkMode ? 'text-orange-300' : 'text-orange-700'}`}>
                    <span className="font-bold">{'\u26a1'} Weakness:</span> {c.weakness_to_exploit}
                  </p>
                )}
                {Array.isArray(c.their_top_keywords) && c.their_top_keywords.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {c.their_top_keywords.map((kw, j) => (
                      <span key={j} className={`text-xs px-2 py-0.5 rounded ${isDarkMode ? 'bg-purple-900/40 text-purple-300' : 'bg-purple-100 text-purple-700'}`}>{kw}</span>
                    ))}
                  </div>
                )}
                {c.their_title_pattern && (
                  <p className={`text-xs mt-2 italic ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>e.g. "{c.their_title_pattern}"</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Keyword Opportunities */}
      {Array.isArray(result.keyword_opportunities) && result.keyword_opportunities.length > 0 && (
        <div className={`p-6 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow`}>
          <h4 className={`text-lg font-bold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{'\ud83d\udd11'} Keyword Opportunities ({result.keyword_opportunities.length})</h4>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className={`border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                  <th className={`text-left p-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Keyword</th>
                  <th className={`text-left p-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Intent</th>
                  <th className={`text-left p-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Competition</th>
                  <th className={`text-left p-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Volume</th>
                  <th className={`text-left p-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Where to use</th>
                </tr>
              </thead>
              <tbody>
                {result.keyword_opportunities.map((kw, i) => (
                  <tr key={i} className={`border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
                    <td className={`p-2 font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{kw.keyword}</td>
                    <td className={`p-2 text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{kw.intent}</td>
                    <td className={`p-2 text-xs`}><span className={`px-2 py-0.5 rounded ${effortBadge(kw.competition)}`}>{kw.competition}</span></td>
                    <td className={`p-2 text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{kw.estimated_volume}</td>
                    <td className={`p-2 text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{kw.where_to_use}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Action Plan */}
      {Array.isArray(result.actions_to_improve_score) && result.actions_to_improve_score.length > 0 && (
        <div className={`p-6 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow`}>
          <h4 className={`text-lg font-bold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{'\ud83d\udccb'} Action Plan (ranked by impact)</h4>
          <div className="space-y-2">
            {result.actions_to_improve_score.map((a, i) => {
              const key = `action-${i}`;
              const applied = appliedKeys[key];
              // Apply only if action provides enough info: page + field + value (or schema code_example with page)
              const actionField = a.field && ['meta_title', 'meta_description', 'schema_markup'].includes(a.field) ? a.field : null;
              const actionPage = a.page || a.page_path || null;
              const actionValue = a.value || a.suggested || (actionField === 'schema_markup' ? a.code_example : null);
              const canApply = a.automatable && actionField && actionPage && actionValue;
              return (
                <div key={i} className={`p-4 rounded-lg flex items-start gap-3 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <span className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-bold ${isDarkMode ? 'bg-purple-700 text-white' : 'bg-purple-200 text-purple-800'}`}>
                    {a.rank ?? i + 1}
                  </span>
                  <div className="flex-1">
                    <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{a.action}</p>
                    <div className="flex gap-2 mt-2 flex-wrap">
                      {a.score_gain != null && <span className={`text-xs px-2 py-0.5 rounded ${isDarkMode ? 'bg-green-900/40 text-green-300' : 'bg-green-100 text-green-700'}`}>+{a.score_gain} pts</span>}
                      {a.effort && <span className={`text-xs px-2 py-0.5 rounded ${effortBadge(a.effort)}`}>{a.effort} effort</span>}
                      {a.automatable && <span className={`text-xs px-2 py-0.5 rounded ${isDarkMode ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-100 text-blue-700'}`}>{'\u26a1'} automatable</span>}
                      {actionPage && <span className={`text-xs px-2 py-0.5 rounded ${isDarkMode ? 'bg-gray-800 text-gray-300' : 'bg-gray-200 text-gray-700'}`}>{actionPage}</span>}
                    </div>
                    {a.code_example && (
                      <pre className={`text-xs mt-2 p-2 rounded font-mono whitespace-pre-wrap break-all ${isDarkMode ? 'bg-gray-900 text-gray-300' : 'bg-white text-gray-700'}`}>{a.code_example}</pre>
                    )}
                    {canApply && (
                      <div className="mt-2">
                        {applied === 'ok' ? (
                          <span className="text-xs font-bold text-green-500">{'\u2705'} Applied to {actionField}</span>
                        ) : applied && applied.startsWith('err:') ? (
                          <>
                            <span className="text-xs text-red-400">{'\u274c'} {applied.slice(4)} </span>
                            <button
                              onClick={() => applyFix(key, actionPage, actionField, actionValue)}
                              disabled={applyingKey === key}
                              className="ml-2 px-3 py-1 rounded text-xs font-medium bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50"
                            >Retry</button>
                          </>
                        ) : (
                          <button
                            onClick={() => applyFix(key, actionPage, actionField, actionValue)}
                            disabled={applyingKey === key}
                            className="px-3 py-1 rounded text-xs font-medium bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50"
                          >
                            {applyingKey === key ? '\u23f3 Applying...' : '\u2728 Apply'}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};


const SEOManagementPanel = ({ isDarkMode, adminToken }) => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  
  // Helper function to get auth token - prioritize adminToken prop
  const getAuthToken = () => {
    if (adminToken) {
      console.log('Using adminToken from props');
      return adminToken;
    }
    const token = localStorage.getItem('user_token') || localStorage.getItem('token') || '';
    console.log('Token from localStorage:', token ? 'Found' : 'Not found');
    return token;
  };
  
  // States for different sections
  const [metaTags, setMetaTags] = useState([]);
  const [seoSettings, setSeoSettings] = useState(null);
  const [sitemapPreview, setSitemapPreview] = useState(null);
  const [sitemapLoading, setSitemapLoading] = useState(false);
  const [deleteMetaTarget, setDeleteMetaTarget] = useState(null);
  const [clear404ModalOpen, setClear404ModalOpen] = useState(false);
  const [delete404Target, setDelete404Target] = useState(null);
  const [deleteRedirectTarget, setDeleteRedirectTarget] = useState(null);
  const [redirects, setRedirects] = useState([]);
  const [socialLinks, setSocialLinks] = useState({
    facebook: '',
    instagram: '',
    twitter: '',
    linkedin: '',
    tiktok: '',
    youtube: ''
  });
  
  // Modal states
  const [showMetaModal, setShowMetaModal] = useState(false);
  const [showRedirectModal, setShowRedirectModal] = useState(false);
  const [editingMeta, setEditingMeta] = useState(null);
  const [newMeta, setNewMeta] = useState({
    page_path: '',
    page_title: '',
    meta_title: '',
    meta_description: '',
    meta_keywords: '',
    og_title: '',
    og_description: '',
    og_image: '',
    canonical_url: '',
    robots: 'index, follow'
  });
  
  const [newRedirect, setNewRedirect] = useState({
    from_url: '',
    to_url: '',
    enabled: true
  });

  const [robotsTxt, setRobotsTxt] = useState('');
  const [sitemapSettings, setSitemapSettings] = useState({
    sitemap_enabled: true,
    sitemap_priority_home: 1.0,
    sitemap_priority_pages: 0.8,
    sitemap_changefreq: 'daily'
  });
  
  // SEO Score state
  const [seoScore, setSeoScore] = useState({
    score: 0,
    percentage: 0,
    successes: [],
    issues: []
  });
  
  // Advanced SEO Settings
  const [linkSettings, setLinkSettings] = useState({
    redirect_attachments: false,
    redirect_orphan_attachments: false,
    nofollow_external_links: false,
    nofollow_image_links: false,
    open_external_new_tab: true
  });
  
  const [imageSettings, setImageSettings] = useState({
    add_missing_alt: true,
    alt_format: '%title% - %sitename%',
    add_missing_title: true,
    title_format: '%title%'
  });
  
  const [monitor404, setMonitor404] = useState({
    enabled: true,
    mode: 'advanced',
    logs: [
      {
        uri: '/old-page-that-does-not-exist',
        hits: 12,
        referer: 'https://google.com',
        lastSeen: '2 hours ago',
        fullUrl: `${window.location.origin}/old-page-that-does-not-exist`
      },
      {
        uri: '/missing-darija-lesson',
        hits: 8,
        referer: 'Direct',
        lastSeen: '1 day ago',
        fullUrl: `${window.location.origin}/missing-darija-lesson`
      },
      {
        uri: '/deleted-game',
        hits: 5,
        referer: 'https://facebook.com',
        lastSeen: '3 days ago',
        fullUrl: `${window.location.origin}/deleted-game`
      }
    ]
  });
  
  // Google Analytics
  const [googleAnalytics, setGoogleAnalytics] = useState({
    tracking_id: '',
    enabled: false,
    stats: null
  });
  
  // Security Settings
  const [securitySettings, setSecuritySettings] = useState({
    xframe_options: true,
    xss_protection: true,
    hsts_enabled: true,
    csp_enabled: false,
    csp_directives: "default-src 'self'; script-src 'self' 'unsafe-inline' 'unsafe-eval'; style-src 'self' 'unsafe-inline' https://fonts.googleapis.com; font-src 'self' https://fonts.gstatic.com; img-src 'self' data: https:; connect-src 'self'"
  });
  
  // 404 Page Editor
  const [page404Settings, setPage404Settings] = useState({
    speech_bubble: "Woof! There's nothing here...",
    heading: "Page Not Found",
    description: "The page you're looking for doesn't exist or has been moved.",
    button1_label: "Go Home",
    button1_path: "/",
    button1_icon: "\ud83c\udfe0",
    button2_label: "Translate",
    button2_path: "/translation",
    button2_icon: "\ud83c\udf10",
    footer_text: "DarijaPro 2026",
    image_url: ""
  });

  // AI Generator
  const [aiGenerating, setAiGenerating] = useState(false);
  
  // General Settings
  const [generalSettings, setGeneralSettings] = useState({
    site_title: 'Darija Pro - Master Moroccan Arabic Online Fast & Easy',
    tagline: 'Learn Moroccan Darija through interactive games, instant translation, and daily lessons. Join 10,000+ learners speaking Arabic fluently!',
    site_icon: '/logo192.png',
    site_logo: '',
    primary_keyword: 'learn darija online',
    secondary_keywords: 'moroccan arabic lessons, darija translation app, speak moroccan dialect, arabic language games, moroccan arabic for beginners'
  });
  
  // Keywords
  const [websiteKeywords, setWebsiteKeywords] = useState([
    'learn darija',
    'moroccan arabic',
    'darija translation',
    'moroccan dialect',
    'arabic games',
    'darija lessons'
  ]);

  useEffect(() => {
    loadSEOData();
  }, []);

  useEffect(() => {
    if (seoSettings) {
      calculateSEOScore();
    }
  }, [metaTags, seoSettings, robotsTxt, sitemapSettings, socialLinks, redirects]);

  const confirmClear404 = async () => {
    setClear404ModalOpen(false);
    try {
      const token = getAuthToken();
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${backendUrl}/api/admin/seo/404-logs`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setMonitor404(prev => ({ ...prev, logs: [] }));
        showNotification('404 logs cleared', 'success');
      } else {
        const txt = await res.text().catch(() => '');
        showNotification(`Failed to clear logs (${res.status}) ${txt.slice(0, 80)}`, 'error');
      }
    } catch (e) {
      showNotification('Failed to clear logs: ' + e.message, 'error');
    }
  };

  const confirmDelete404 = async () => {
    const target = delete404Target;
    setDelete404Target(null);
    if (!target?.uri) return;
    try {
      const token = getAuthToken();
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${backendUrl}/api/admin/seo/404-logs/delete-one`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ uri: target.uri })
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.deleted) {
        setMonitor404(prev => ({ ...prev, logs: (prev.logs || []).filter(l => l.uri !== target.uri) }));
        showNotification('404 entry deleted', 'success');
      } else {
        showNotification(data.message || `Failed to delete (${res.status})`, 'error');
      }
    } catch (e) {
      showNotification('Failed to delete: ' + e.message, 'error');
    }
  };

  const loadSitemapPreview = async () => {
    setSitemapLoading(true);
    try {
      const token = getAuthToken();
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${backendUrl}/api/admin/seo/sitemap-preview`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSitemapPreview(data);
      } else {
        showNotification('Failed to load sitemap preview', 'error');
      }
    } catch (e) {
      showNotification('Error loading sitemap preview', 'error');
    }
    setSitemapLoading(false);
  };

  const loadSEOData = async () => {
    setLoading(true);
    try {
      const token = getAuthToken();
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      
      // Load SEO settings
      const settingsRes = await fetch(`${backendUrl}/api/admin/seo/settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (settingsRes.ok) {
        const settings = await settingsRes.json();
        setSeoSettings(settings);
        setRobotsTxt(settings.robots_txt || '');
        setSitemapSettings({
          sitemap_enabled: settings.sitemap_enabled ?? true,
          sitemap_priority_home: settings.sitemap_priority_home ?? 1.0,
          sitemap_priority_pages: settings.sitemap_priority_pages ?? 0.8,
          sitemap_changefreq: settings.sitemap_changefreq || 'daily'
        });
        
        // Load social links from site settings (if stored there)
        setSocialLinks({
          facebook: settings.facebook_url || '',
          instagram: settings.instagram_url || '',
          twitter: settings.twitter_url || '',
          linkedin: settings.linkedin_url || '',
          tiktok: settings.tiktok_url || '',
          youtube: settings.youtube_url || ''
        });

        // Load general settings from DB (prevents reverting on save)
        setGeneralSettings({
          site_title: settings.site_name || 'Darija Pro - Master Moroccan Arabic Online Fast & Easy',
          tagline: settings.site_description || 'Learn Moroccan Darija through interactive games, instant translation, and daily lessons. Join 10,000+ learners speaking Arabic fluently!',
          site_icon: settings.default_og_image || '/logo192.png',
          site_logo: settings.site_logo || '',
          primary_keyword: settings.primary_keyword || 'learn darija online',
          secondary_keywords: settings.secondary_keywords || 'moroccan arabic lessons, darija translation app, speak moroccan dialect, arabic language games, moroccan arabic for beginners'
        });

        // Load keywords from DB
        if (settings.site_keywords) {
          const kw = settings.site_keywords.split(',').map(k => k.trim()).filter(k => k);
          if (kw.length > 0) setWebsiteKeywords(kw);
        }

        // Load security settings from DB
        setSecuritySettings(prev => ({
          ...prev,
          xframe_options: settings.enable_xframe_protection ?? true,
          xss_protection: settings.enable_xss_protection ?? true,
          hsts_enabled: settings.enable_ssl_redirect ?? true,
          csp_enabled: settings.csp_enabled ?? false,
          csp_directives: settings.csp_directives || prev.csp_directives
        }));
      }
      
      // Load meta tags
      const metaRes = await fetch(`${backendUrl}/api/admin/seo/meta-tags`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (metaRes.ok) {
        const meta = await metaRes.json();
        setMetaTags(Array.isArray(meta) ? meta : []);
      }
      
      // Load redirects
      const redirectsRes = await fetch(`${backendUrl}/api/admin/seo/redirects`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (redirectsRes.ok) {
        const redir = await redirectsRes.json();
        setRedirects(Array.isArray(redir) ? redir : []);
      }

      // Load 404 page editor settings (public endpoint) - wrapped separately so it doesn't break other loads
      try {
        const page404Res = await fetch(`${backendUrl}/api/seo/404-page-settings`);
        if (page404Res.ok) {
          const page404Data = await page404Res.json();
          setPage404Settings(prev => ({ ...prev, ...page404Data }));
        }
      } catch (e) { console.log('404 page settings not loaded (non-critical)'); }

      // Load 404 error logs
      try {
        const logsRes = await fetch(`${backendUrl}/api/admin/seo/404-logs`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (logsRes.ok) {
          const logs = await logsRes.json();
          if (Array.isArray(logs)) {
            setMonitor404(prev => ({ ...prev, logs }));
          }
        }
      } catch (e) { console.log('404 logs not loaded (non-critical)'); }
    } catch (error) {
      console.error('Error loading SEO data:', error);
      showNotification('Error loading SEO data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: '', type: '' }), 3000);
  };

  const calculateSEOScore = () => {
    let score = 0;
    const maxScore = 100;
    const successes = [];
    const issues = [];

    // Check SEO settings (20 points)
    if (seoSettings) {
      if (seoSettings.site_name && seoSettings.site_name.length > 10) {
        score += 5;
        successes.push('✅ Site name configured');
      } else {
        issues.push('❌ Site name too short or missing');
      }

      if (seoSettings.site_description && seoSettings.site_description.length > 50) {
        score += 5;
        successes.push('✅ Site description configured');
      } else {
        issues.push('❌ Site description too short or missing');
      }

      if (robotsTxt && robotsTxt.length > 100) {
        score += 5;
        successes.push('✅ Robots.txt configured');
      } else {
        issues.push('❌ Robots.txt missing or incomplete');
      }

      if (sitemapSettings.sitemap_enabled) {
        score += 5;
        successes.push('✅ Sitemap enabled');
      } else {
        issues.push('❌ Sitemap disabled');
      }
    }

    // Check meta tags (30 points)
    if (metaTags.length >= 5) {
      score += 15;
      successes.push(`✅ ${metaTags.length} pages have meta tags`);
    } else if (metaTags.length >= 3) {
      score += 10;
      issues.push(`⚠️ Only ${metaTags.length} pages have meta tags (need 5+)`);
    } else {
      issues.push(`❌ Only ${metaTags.length} pages have meta tags`);
    }

    // Check meta quality
    let qualityCount = 0;
    metaTags.forEach(tag => {
      if (tag.meta_title && tag.meta_title.length >= 30 && tag.meta_title.length <= 60 &&
          tag.meta_description && tag.meta_description.length >= 120 && tag.meta_description.length <= 160) {
        qualityCount++;
      }
    });

    if (metaTags.length > 0) {
      if (qualityCount >= metaTags.length * 0.7) {
        score += 15;
        successes.push('✅ High-quality meta tags (optimized length)');
      } else if (qualityCount >= metaTags.length * 0.5) {
        score += 10;
        issues.push('⚠️ Some meta tags need length optimization');
      } else {
        issues.push('❌ Meta tags need optimization (title 30-60, desc 120-160 chars)');
      }
    }

    // Check social media links (15 points)
    const activeSocialLinks = Object.values(socialLinks).filter(link => link && link.startsWith('http')).length;
    if (activeSocialLinks >= 4) {
      score += 15;
      successes.push(`✅ ${activeSocialLinks}/6 social media links configured`);
    } else if (activeSocialLinks >= 2) {
      score += 10;
      issues.push(`⚠️ Only ${activeSocialLinks}/6 social media links (need 4+)`);
    } else {
      issues.push(`❌ Only ${activeSocialLinks}/6 social media links configured`);
    }

    // Check redirects (10 points)
    if (redirects.length > 0) {
      score += 10;
      successes.push(`✅ ${redirects.length} redirects configured`);
    } else {
      score += 5;
      issues.push('⚠️ No redirects configured (add when needed)');
    }

    // Technical SEO (25 points)
    if (seoSettings) {
      const techChecks = [
        seoSettings.enable_security_headers,
        seoSettings.enable_ssl_redirect,
        seoSettings.enable_xframe_protection,
        seoSettings.enable_xss_protection,
        seoSettings.block_admin_indexing
      ].filter(Boolean).length;

      score += techChecks * 5;
      if (techChecks >= 4) {
        successes.push('✅ Strong technical SEO (security headers)');
      } else {
        issues.push(`⚠️ Technical SEO needs improvement (${techChecks}/5)`);
      }
    }

    const percentage = Math.round((score / maxScore) * 100);
    setSeoScore({ score, percentage, successes, issues });
  };

  const handleSaveSeoSettings = async () => {
    try {
      const token = getAuthToken();
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      
      if (!token) {
        showNotification('❌ Please login first (token not found)', 'error');
        console.error('No token found in localStorage. Keys:', Object.keys(localStorage));
        return;
      }
      
      console.log('Token found, attempting to save...');
      
      const formData = new FormData();
      formData.append('site_name', generalSettings.site_title || seoSettings?.site_name || 'Darija Pro - Learn Moroccan Arabic');
      formData.append('site_description', generalSettings.tagline || seoSettings?.site_description || 'Master Moroccan Darija with interactive lessons');
      formData.append('site_keywords', websiteKeywords.join(', ') || seoSettings?.site_keywords || 'darija, moroccan arabic, learn darija');
      formData.append('default_og_image', generalSettings.site_icon || seoSettings?.default_og_image || '');
      formData.append('site_logo', generalSettings.site_logo || '');
      formData.append('primary_keyword', generalSettings.primary_keyword || '');
      formData.append('secondary_keywords', generalSettings.secondary_keywords || '');
      formData.append('google_analytics_id', seoSettings?.google_analytics_id || '');
      formData.append('google_search_console_verification', seoSettings?.google_search_console_verification || '');
      formData.append('robots_txt', robotsTxt || '');
      formData.append('sitemap_enabled', sitemapSettings.sitemap_enabled ? 'true' : 'false');
      formData.append('sitemap_priority_home', sitemapSettings.sitemap_priority_home.toString());
      formData.append('sitemap_priority_pages', sitemapSettings.sitemap_priority_pages.toString());
      formData.append('sitemap_changefreq', sitemapSettings.sitemap_changefreq);
      formData.append('enable_ssl_redirect', securitySettings.hsts_enabled ? 'true' : 'false');
      formData.append('enable_www_redirect', 'false');
      formData.append('enable_security_headers', 'true');
      formData.append('block_admin_indexing', 'true');
      formData.append('block_api_indexing', 'true');
      formData.append('enable_xframe_protection', securitySettings.xframe_options ? 'true' : 'false');
      formData.append('enable_xss_protection', securitySettings.xss_protection ? 'true' : 'false');
      formData.append('enable_content_type_nosniff', 'true');
      formData.append('enable_referrer_policy', 'true');
      formData.append('referrer_policy_value', 'strict-origin-when-cross-origin');
      formData.append('csp_enabled', securitySettings.csp_enabled ? 'true' : 'false');
      formData.append('csp_directives', securitySettings.csp_directives || '');
      
      // Add social links
      formData.append('facebook_url', socialLinks.facebook || '');
      formData.append('instagram_url', socialLinks.instagram || '');
      formData.append('twitter_url', socialLinks.twitter || '');
      formData.append('linkedin_url', socialLinks.linkedin || '');
      formData.append('tiktok_url', socialLinks.tiktok || '');
      formData.append('youtube_url', socialLinks.youtube || '');
      
      console.log('Saving SEO settings to:', `${backendUrl}/api/admin/seo/settings`);
      
      const res = await fetch(`${backendUrl}/api/admin/seo/settings`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (res.ok) {
        showNotification('✅ SEO settings saved successfully!', 'success');
        await loadSEOData();
        calculateSEOScore();
      } else {
        const errorText = await res.text();
        console.error('Save error:', errorText);
        showNotification(`❌ Error: ${res.status} - ${errorText.substring(0, 50)}`, 'error');
      }
    } catch (error) {
      console.error('Error saving SEO settings:', error);
      showNotification(`❌ Error: ${error.message}`, 'error');
    }
  };

  const handleSave404Page = async () => {
    try {
      const token = getAuthToken();
      const backendUrl = process.env.REACT_APP_BACKEND_URL;

      // Only send the editable fields (strip _id, id, created_at, etc.)
      const payload = {
        speech_bubble: page404Settings.speech_bubble || '',
        heading: page404Settings.heading || '',
        description: page404Settings.description || '',
        button1_label: page404Settings.button1_label || '',
        button1_path: page404Settings.button1_path || '/',
        button1_icon: page404Settings.button1_icon || '',
        button2_label: page404Settings.button2_label || '',
        button2_path: page404Settings.button2_path || '/',
        button2_icon: page404Settings.button2_icon || '',
        footer_text: page404Settings.footer_text || '',
        image_url: page404Settings.image_url || ''
      };

      const res = await fetch(`${backendUrl}/api/admin/seo/404-page-settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        showNotification('404 page saved successfully!', 'success');
      } else {
        const errText = await res.text();
        console.error('Save 404 error:', res.status, errText);
        showNotification(`Error ${res.status}: ${errText.substring(0, 80)}`, 'error');
      }
    } catch (error) {
      console.error('Save 404 exception:', error);
      showNotification('Network error: ' + error.message, 'error');
    }
  };

  const handleSaveMetaTag = async () => {
    try {
      const token = getAuthToken();
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      
      const formData = new FormData();
      Object.keys(newMeta).forEach(key => {
        formData.append(key, newMeta[key] || '');
      });
      
      const url = editingMeta 
        ? `${backendUrl}/api/admin/seo/meta-tags/${editingMeta.id}`
        : `${backendUrl}/api/admin/seo/meta-tags`;
      
      const res = await fetch(url, {
        method: editingMeta ? 'PUT' : 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (res.ok) {
        showNotification(`Meta tag ${editingMeta ? 'updated' : 'created'} successfully!`, 'success');
        setShowMetaModal(false);
        setEditingMeta(null);
        setNewMeta({
          page_path: '',
          page_title: '',
          meta_title: '',
          meta_description: '',
          meta_keywords: '',
          og_title: '',
          og_description: '',
          og_image: '',
          twitter_card: 'summary_large_image',
          twitter_title: '',
          twitter_description: '',
          twitter_image: '',
          canonical_url: '',
          robots: 'index, follow',
          schema_markup: ''
        });
        loadSEOData();
      } else {
        const errorData = await res.json().catch(() => ({}));
        showNotification(errorData.detail || 'Error saving meta tag', 'error');
      }
    } catch (error) {
      console.error('Error saving meta tag:', error);
      showNotification('Error saving meta tag', 'error');
    }
  };

  const handleDeleteMetaTag = (metaId) => {
    const target = metaTags.find(m => m.id === metaId);
    setDeleteMetaTarget(target || { id: metaId });
  };

  const confirmDeleteMetaTag = async () => {
    const target = deleteMetaTarget;
    setDeleteMetaTarget(null);
    if (!target?.id) return;
    try {
      const token = getAuthToken();
      const backendUrl = process.env.REACT_APP_BACKEND_URL;

      const res = await fetch(`${backendUrl}/api/admin/seo/meta-tags/${target.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        showNotification('Meta tag deleted successfully!', 'success');
        loadSEOData();
      } else {
        showNotification('Error deleting meta tag', 'error');
      }
    } catch (error) {
      console.error('Error deleting meta tag:', error);
      showNotification('Error deleting meta tag', 'error');
    }
  };

  const handleSaveRedirect = async () => {
    try {
      const token = getAuthToken();
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      
      const formData = new FormData();
      formData.append('from_url', newRedirect.from_url);
      formData.append('to_url', newRedirect.to_url);
      formData.append('enabled', newRedirect.enabled ? 'true' : 'false');
      
      const res = await fetch(`${backendUrl}/api/admin/seo/redirects`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (res.ok) {
        showNotification('Redirect created successfully!', 'success');
        setShowRedirectModal(false);
        setNewRedirect({ from_url: '', to_url: '', enabled: true });
        loadSEOData();
      } else {
        showNotification('Error creating redirect', 'error');
      }
    } catch (error) {
      console.error('Error creating redirect:', error);
      showNotification('Error creating redirect', 'error');
    }
  };

  const handleDeleteRedirect = (redirectId) => {
    const target = redirects.find(r => r.id === redirectId);
    setDeleteRedirectTarget(target || { id: redirectId });
  };

  const confirmDeleteRedirect = async () => {
    const target = deleteRedirectTarget;
    setDeleteRedirectTarget(null);
    if (!target?.id) return;
    try {
      const token = getAuthToken();
      const backendUrl = process.env.REACT_APP_BACKEND_URL;

      const res = await fetch(`${backendUrl}/api/admin/seo/redirects/${target.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        showNotification('Redirect deleted successfully!', 'success');
        loadSEOData();
      } else {
        showNotification('Error deleting redirect', 'error');
      }
    } catch (error) {
      console.error('Error deleting redirect:', error);
      showNotification('Error deleting redirect', 'error');
    }
  };

  const tabs = [
    { id: 'dashboard', label: '\ud83c\udfaf Dashboard', icon: '\ud83c\udfaf' },
    { id: 'general', label: '\u2699\ufe0f General', icon: '\u2699\ufe0f' },
    { id: 'keywords', label: '\ud83d\udd11 Keywords', icon: '\ud83d\udd11' },
    { id: 'meta-tags', label: '\ud83c\udff7\ufe0f Meta Tags', icon: '\ud83c\udff7\ufe0f' },
    { id: 'schema', label: '\ud83d\udcdd Schema', icon: '\ud83d\udcdd' },
    { id: 'robots', label: '\ud83e\udd16 Robots.txt', icon: '\ud83e\udd16' },
    { id: 'sitemap', label: '\ud83d\uddfa\ufe0f Sitemap', icon: '\ud83d\uddfa\ufe0f' },
    { id: 'social', label: '\ud83d\udcf1 Social', icon: '\ud83d\udcf1' },
    { id: 'redirects', label: '\u21a9\ufe0f Redirects', icon: '\u21a9\ufe0f' },
    { id: 'monitor', label: '\ud83d\udea8 404 Monitor', icon: '\ud83d\udea8' },
    { id: 'security', label: '\ud83d\udd12 Security', icon: '\ud83d\udd12' },
    { id: 'seo-tools', label: '\ud83d\udee0\ufe0f SEO Tools', icon: '\ud83d\udee0\ufe0f' },
    { id: 'analytics', label: '\ud83d\udcc8 Analytics', icon: '\ud83d\udcc8' },
    { id: 'api-providers', label: '\ud83d\udd0c API', icon: '\ud83d\udd0c' }
  ];

  return (
    <div className={`min-h-screen p-4 md:p-6 ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className={`text-3xl md:text-4xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            🔍 SEO Management Center
          </h1>
          <p className={`text-sm md:text-lg ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Complete control over your website's search engine optimization
          </p>
        </div>

        {/* Notification */}
        {notification.show && (
          <div className={`fixed top-4 right-4 z-50 px-6 py-3 rounded-lg shadow-lg ${
            notification.type === 'success' ? 'bg-green-500' : 'bg-red-500'
          } text-white font-medium animate-bounce`}>
            {notification.message}
          </div>
        )}

        {/* Clear All 404 Logs Confirmation Modal */}
        {clear404ModalOpen && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
            onClick={() => setClear404ModalOpen(false)}
          >
            <div
              className={`w-full max-w-md rounded-2xl shadow-2xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex items-center justify-center mb-4">
                  <div className={`w-16 h-16 rounded-full flex items-center justify-center ${isDarkMode ? 'bg-red-900/40' : 'bg-red-100'}`}>
                    <span className="text-3xl">{'🗑️'}</span>
                  </div>
                </div>
                <h3 className={`text-xl font-bold text-center mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  Clear All 404 Logs?
                </h3>
                <p className={`text-sm text-center mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  All {monitor404?.logs?.length || 0} logged 404 errors will be removed.
                </p>
                <p className={`text-xs text-center mb-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Future 404 hits will start being logged again from zero.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => setClear404ModalOpen(false)}
                    className={`flex-1 px-4 py-3 rounded-lg font-medium transition-colors ${isDarkMode ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmClear404}
                    className="flex-1 px-4 py-3 rounded-lg font-medium bg-red-600 text-white hover:bg-red-700 transition-colors"
                  >
                    Clear All
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete Single 404 Log Modal */}
        {delete404Target && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
            onClick={() => setDelete404Target(null)}
          >
            <div
              className={`w-full max-w-md rounded-2xl shadow-2xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex items-center justify-center mb-4">
                  <div className={`w-16 h-16 rounded-full flex items-center justify-center ${isDarkMode ? 'bg-red-900/40' : 'bg-red-100'}`}>
                    <span className="text-3xl">{'🗑️'}</span>
                  </div>
                </div>
                <h3 className={`text-xl font-bold text-center mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  Delete this 404 log?
                </h3>
                <p className={`text-sm text-center mb-2 font-mono break-all ${isDarkMode ? 'text-red-300' : 'text-red-700'}`}>
                  {delete404Target.uri}
                </p>
                <p className={`text-xs text-center mb-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Hits: {delete404Target.hits || 0}. The other {Math.max(0, (monitor404?.logs?.length || 1) - 1)} log(s) stay.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => setDelete404Target(null)}
                    className={`flex-1 px-4 py-3 rounded-lg font-medium transition-colors ${isDarkMode ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmDelete404}
                    className="flex-1 px-4 py-3 rounded-lg font-medium bg-red-600 text-white hover:bg-red-700 transition-colors"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete Redirect Confirmation Modal */}
        {deleteRedirectTarget && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
            onClick={() => setDeleteRedirectTarget(null)}
          >
            <div
              className={`w-full max-w-md rounded-2xl shadow-2xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex items-center justify-center mb-4">
                  <div className={`w-16 h-16 rounded-full flex items-center justify-center ${isDarkMode ? 'bg-red-900/40' : 'bg-red-100'}`}>
                    <span className="text-3xl">{'🗑️'}</span>
                  </div>
                </div>
                <h3 className={`text-xl font-bold text-center mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  Delete Redirect?
                </h3>
                {deleteRedirectTarget.from_url && (
                  <p className={`text-sm text-center mb-1 font-mono break-all ${isDarkMode ? 'text-purple-300' : 'text-purple-700'}`}>
                    {deleteRedirectTarget.from_url} → {deleteRedirectTarget.to_url}
                  </p>
                )}
                <p className={`text-xs text-center mb-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Visitors hitting <code>{deleteRedirectTarget.from_url}</code> will get a 404 instead of being redirected.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => setDeleteRedirectTarget(null)}
                    className={`flex-1 px-4 py-3 rounded-lg font-medium transition-colors ${isDarkMode ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmDeleteRedirect}
                    className="flex-1 px-4 py-3 rounded-lg font-medium bg-red-600 text-white hover:bg-red-700 transition-colors"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete Meta Tag Confirmation Modal */}
        {deleteMetaTarget && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
            onClick={() => setDeleteMetaTarget(null)}
          >
            <div
              className={`w-full max-w-md rounded-2xl shadow-2xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex items-center justify-center mb-4">
                  <div className={`w-16 h-16 rounded-full flex items-center justify-center ${isDarkMode ? 'bg-red-900/40' : 'bg-red-100'}`}>
                    <span className="text-3xl">{'🗑️'}</span>
                  </div>
                </div>
                <h3 className={`text-xl font-bold text-center mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  Delete Meta Tag?
                </h3>
                {deleteMetaTarget.page_path && (
                  <p className={`text-sm text-center mb-2 font-mono ${isDarkMode ? 'text-purple-300' : 'text-purple-700'}`}>
                    {deleteMetaTarget.page_path}
                  </p>
                )}
                {deleteMetaTarget.meta_title && (
                  <p className={`text-xs text-center mb-2 italic ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    "{deleteMetaTarget.meta_title}"
                  </p>
                )}
                <p className={`text-xs text-center mb-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  This will remove the page's custom meta tags. The page will fall back to default settings until you re-add or regenerate it.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => setDeleteMetaTarget(null)}
                    className={`flex-1 px-4 py-3 rounded-lg font-medium transition-colors ${isDarkMode ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmDeleteMetaTag}
                    className="flex-1 px-4 py-3 rounded-lg font-medium bg-red-600 text-white hover:bg-red-700 transition-colors"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Main Layout - Sidebar + Content */}
        <div className="flex gap-6">
          {/* Left Sidebar - Vertical Tabs */}
          <div className={`w-64 flex-shrink-0 rounded-xl shadow-lg overflow-hidden ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="p-4">
              <h3 className={`text-sm font-bold mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                NAVIGATION
              </h3>
              <div className="space-y-1">
                {tabs.map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-all text-left ${
                      activeTab === tab.id
                        ? isDarkMode
                          ? 'bg-purple-600 text-white shadow-lg'
                          : 'bg-purple-500 text-white shadow-lg'
                        : isDarkMode
                          ? 'text-gray-300 hover:bg-gray-700 hover:text-white'
                          : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                    }`}
                  >
                    <span className="text-xl">{tab.icon}</span>
                    <span className="text-sm">{tab.label.split(' ').slice(1).join(' ')}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Right Content Area */}
          <div className={`flex-1 rounded-xl shadow-lg p-6 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
          {loading ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">⏳</div>
              <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Loading SEO data...</p>
            </div>
          ) : (
            <>
              {/* General Settings Tab */}
              {activeTab === 'general' && (
                <div>
                  <h2 className={`text-2xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    ⚙️ General Settings
                  </h2>
                  <p className={`mb-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    Configure your website's basic SEO settings
                  </p>
                  
                  <div className="space-y-6">
                    <div className={`p-6 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}>
                      <h3 className={`text-lg font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        🌐 Site Identity
                      </h3>
                      
                      <div className="space-y-4">
                        <div>
                          <label className={`block mb-2 font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            Site Title
                          </label>
                          <input
                            type="text"
                            value={generalSettings.site_title}
                            onChange={(e) => setGeneralSettings({...generalSettings, site_title: e.target.value})}
                            placeholder="Darija Pro - Learn Moroccan Arabic Online"
                            className={`w-full p-3 rounded-lg ${
                              isDarkMode 
                                ? 'bg-gray-800 text-white border-gray-600' 
                                : 'bg-white text-gray-900 border-gray-300'
                            } border-2`}
                          />
                          <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            This appears in browser tabs and search results
                          </p>
                        </div>
                        
                        <div>
                          <label className={`block mb-2 font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            Tagline / Description
                          </label>
                          <input
                            type="text"
                            value={generalSettings.tagline}
                            onChange={(e) => setGeneralSettings({...generalSettings, tagline: e.target.value})}
                            placeholder="Master Moroccan Darija with interactive games"
                            className={`w-full p-3 rounded-lg ${
                              isDarkMode 
                                ? 'bg-gray-800 text-white border-gray-600' 
                                : 'bg-white text-gray-900 border-gray-300'
                            } border-2`}
                          />
                          <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            Brief description of your website
                          </p>
                        </div>
                        
                        <div>
                          <label className={`block mb-2 font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            Site Icon (Favicon)
                          </label>
                          <div className="flex gap-2">
                            <input
                              type="url"
                              value={generalSettings.site_icon}
                              onChange={(e) => setGeneralSettings({...generalSettings, site_icon: e.target.value})}
                              placeholder="https://your-site.com/favicon.ico"
                              className={`flex-1 p-3 rounded-lg ${
                                isDarkMode
                                  ? 'bg-gray-800 text-white border-gray-600'
                                  : 'bg-white text-gray-900 border-gray-300'
                              } border-2`}
                            />
                            <label className="px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 cursor-pointer font-medium text-sm flex items-center gap-1">
                              Upload
                              <input
                                type="file"
                                accept="image/*,.ico"
                                className="hidden"
                                onChange={async (e) => {
                                  const file = e.target.files[0];
                                  if (!file) return;
                                  try {
                                    const token = getAuthToken();
                                    const backendUrl = process.env.REACT_APP_BACKEND_URL;
                                    const fd = new FormData();
                                    fd.append('file', file);
                                    fd.append('image_type', 'favicon');
                                    const res = await fetch(`${backendUrl}/api/admin/seo/upload-image`, {
                                      method: 'POST',
                                      headers: { 'Authorization': `Bearer ${token}` },
                                      body: fd
                                    });
                                    if (res.ok) {
                                      const data = await res.json();
                                      setGeneralSettings(prev => ({...prev, site_icon: data.url}));
                                      showNotification('Favicon uploaded successfully!', 'success');
                                    } else {
                                      const err = await res.json().catch(() => ({}));
                                      showNotification(err.detail || 'Upload failed', 'error');
                                    }
                                  } catch (err) {
                                    showNotification('Upload failed: ' + err.message, 'error');
                                  }
                                  e.target.value = '';
                                }}
                              />
                            </label>
                          </div>
                          {generalSettings.site_icon && (
                            <div className="mt-2 flex items-center gap-2">
                              <img src={generalSettings.site_icon} alt="Favicon preview" className="w-8 h-8 object-contain border rounded" onError={(e) => e.target.style.display='none'} />
                              <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Preview</span>
                            </div>
                          )}
                          <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            Small icon shown in browser tabs (16x16px or 32x32px). Paste URL or upload file.
                          </p>
                        </div>

                        <div>
                          <label className={`block mb-2 font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            Site Logo
                          </label>
                          <div className="flex gap-2">
                            <input
                              type="url"
                              value={generalSettings.site_logo}
                              onChange={(e) => setGeneralSettings({...generalSettings, site_logo: e.target.value})}
                              placeholder="https://your-site.com/logo.png"
                              className={`flex-1 p-3 rounded-lg ${
                                isDarkMode
                                  ? 'bg-gray-800 text-white border-gray-600'
                                  : 'bg-white text-gray-900 border-gray-300'
                              } border-2`}
                            />
                            <label className="px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 cursor-pointer font-medium text-sm flex items-center gap-1">
                              Upload
                              <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={async (e) => {
                                  const file = e.target.files[0];
                                  if (!file) return;
                                  try {
                                    const token = getAuthToken();
                                    const backendUrl = process.env.REACT_APP_BACKEND_URL;
                                    const fd = new FormData();
                                    fd.append('file', file);
                                    fd.append('image_type', 'logo');
                                    const res = await fetch(`${backendUrl}/api/admin/seo/upload-image`, {
                                      method: 'POST',
                                      headers: { 'Authorization': `Bearer ${token}` },
                                      body: fd
                                    });
                                    if (res.ok) {
                                      const data = await res.json();
                                      setGeneralSettings(prev => ({...prev, site_logo: data.url}));
                                      showNotification('Logo uploaded successfully!', 'success');
                                    } else {
                                      const err = await res.json().catch(() => ({}));
                                      showNotification(err.detail || 'Upload failed', 'error');
                                    }
                                  } catch (err) {
                                    showNotification('Upload failed: ' + err.message, 'error');
                                  }
                                  e.target.value = '';
                                }}
                              />
                            </label>
                          </div>
                          {generalSettings.site_logo && (
                            <div className="mt-2 flex items-center gap-2">
                              <img src={generalSettings.site_logo} alt="Logo preview" className="h-10 object-contain border rounded" onError={(e) => e.target.style.display='none'} />
                              <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Preview</span>
                            </div>
                          )}
                          <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            Logo displayed in sidebar/navbar. Paste URL or upload file.
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    <div className={`p-6 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}>
                      <h3 className={`text-lg font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        🔑 Primary Keywords
                      </h3>
                      
                      <div className="space-y-4">
                        <div>
                          <label className={`block mb-2 font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            Main Keyword (Most Important)
                          </label>
                          <input
                            type="text"
                            value={generalSettings.primary_keyword}
                            onChange={(e) => setGeneralSettings({...generalSettings, primary_keyword: e.target.value})}
                            placeholder="learn darija"
                            className={`w-full p-3 rounded-lg ${
                              isDarkMode 
                                ? 'bg-gray-800 text-white border-gray-600' 
                                : 'bg-white text-gray-900 border-gray-300'
                            } border-2`}
                          />
                          <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            The #1 keyword you want to rank for
                          </p>
                        </div>
                        
                        <div>
                          <label className={`block mb-2 font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            Secondary Keywords (Comma-separated)
                          </label>
                          <textarea
                            value={generalSettings.secondary_keywords}
                            onChange={(e) => setGeneralSettings({...generalSettings, secondary_keywords: e.target.value})}
                            rows={3}
                            placeholder="moroccan arabic, darija translation, learn moroccan dialect"
                            className={`w-full p-3 rounded-lg ${
                              isDarkMode 
                                ? 'bg-gray-800 text-white border-gray-600' 
                                : 'bg-white text-gray-900 border-gray-300'
                            } border-2`}
                          />
                          <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            Additional keywords separated by commas
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    <div className={`p-6 rounded-lg ${isDarkMode ? 'bg-blue-900/30' : 'bg-blue-50'}`}>
                      <h3 className={`text-lg font-bold mb-3 ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                        📍 Your Website URLs Structure
                      </h3>
                      <div className={`space-y-2 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        <p><strong>Homepage:</strong> {window.location.host}/</p>
                        <p><strong>Translation:</strong> {window.location.host}/translation</p>
                        <p><strong>Games:</strong> {window.location.host}/games</p>
                        <p><strong>Daily Words:</strong> {window.location.host}/daily-words</p>
                        <p><strong>Membership:</strong> {window.location.host}/membership</p>
                        <p><strong>Categories:</strong> {window.location.host}/categories</p>
                        <p className="mt-3 text-xs italic">This clean URL structure helps SEO!</p>
                      </div>
                    </div>
                  </div>
                  
                  <button
                    onClick={handleSaveSeoSettings}
                    className="mt-6 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
                  >
                    💾 Save General Settings
                  </button>
                </div>
              )}

              {/* Keywords Tab */}
              {activeTab === 'keywords' && (
                <div>
                  <h2 className={`text-2xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    🔑 Website Keywords
                  </h2>
                  <p className={`mb-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    Manage keywords that help your website rank in search engines
                  </p>
                  
                  <div className="space-y-6">
                    <div className={`p-6 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}>
                      <h3 className={`text-lg font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        📋 Current Keywords
                      </h3>
                      
                      <div className="flex flex-wrap gap-2 mb-4">
                        {websiteKeywords.map((keyword, idx) => (
                          <div 
                            key={idx}
                            className={`px-4 py-2 rounded-full flex items-center gap-2 ${
                              isDarkMode ? 'bg-purple-600 text-white' : 'bg-purple-100 text-purple-700'
                            }`}
                          >
                            <span>{keyword}</span>
                            <button
                              onClick={() => {
                                setWebsiteKeywords(websiteKeywords.filter((_, i) => i !== idx));
                              }}
                              className="text-white hover:text-red-300"
                            >
                              ✕
                            </button>
                          </div>
                        ))}
                      </div>
                      
                      <div className="flex gap-2">
                        <input
                          type="text"
                          id="new-keyword"
                          placeholder="Add new keyword..."
                          className={`flex-1 p-3 rounded-lg ${
                            isDarkMode 
                              ? 'bg-gray-800 text-white border-gray-600' 
                              : 'bg-white text-gray-900 border-gray-300'
                          } border-2`}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              const input = e.target;
                              if (input.value.trim()) {
                                setWebsiteKeywords([...websiteKeywords, input.value.trim()]);
                                input.value = '';
                              }
                            }
                          }}
                        />
                        <button
                          onClick={() => {
                            const input = document.getElementById('new-keyword');
                            if (input.value.trim()) {
                              setWebsiteKeywords([...websiteKeywords, input.value.trim()]);
                              input.value = '';
                            }
                          }}
                          className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                        >
                          ➕ Add
                        </button>
                      </div>
                    </div>
                    
                    <div className={`p-6 rounded-lg ${isDarkMode ? 'bg-green-900/30' : 'bg-green-50'}`}>
                      <h3 className={`text-lg font-bold mb-3 ${isDarkMode ? 'text-green-300' : 'text-green-700'}`}>
                        💡 Keyword Tips
                      </h3>
                      <ul className={`space-y-2 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        <li>✅ Use 2-3 word phrases (long-tail keywords)</li>
                        <li>✅ Include location: "learn darija morocco"</li>
                        <li>✅ Think like users: "how to learn moroccan arabic"</li>
                        <li>✅ Use variations: "darija lessons", "moroccan dialect course"</li>
                        <li>✅ Check competitors' keywords</li>
                        <li>✅ Focus on 5-10 main keywords</li>
                      </ul>
                    </div>
                  </div>
                  
                  <button
                    onClick={handleSaveSeoSettings}
                    className="mt-6 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
                  >
                    💾 Save Keywords
                  </button>
                </div>
              )}

              {/* Meta Tags Tab */}
              {activeTab === 'meta-tags' && (
                <div>
                  <div className="flex justify-between items-center mb-6">
                    <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      Meta Tags Manager
                    </h2>
                    <button
                      onClick={() => {
                        setEditingMeta(null);
                        setNewMeta({
                          page_path: '',
                          page_title: '',
                          meta_title: '',
                          meta_description: '',
                          meta_keywords: '',
                          og_title: '',
                          og_description: '',
                          og_image: '',
                          twitter_card: 'summary_large_image',
                          twitter_title: '',
                          twitter_description: '',
                          twitter_image: '',
                          canonical_url: '',
                          robots: 'index, follow',
                          schema_markup: ''
                        });
                        setShowMetaModal(true);
                      }}
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
                    >
                      ➕ Add Page Meta Tags
                    </button>
                  </div>
                  
                  <div className="space-y-4">
                    {metaTags.length === 0 ? (
                      <div className={`text-center py-12 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        <div className="text-6xl mb-4">📄</div>
                        <p className="text-xl mb-2">No meta tags configured yet</p>
                        <p className="text-sm">Click "Add Page Meta Tags" to create your first one</p>
                      </div>
                    ) : (
                      metaTags.map(meta => (
                        <div
                          key={meta.id}
                          className={`p-4 rounded-lg border-l-4 border-purple-500 ${
                            isDarkMode ? 'bg-gray-700' : 'bg-gray-50'
                          }`}
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <h3 className={`text-lg font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                {meta.page_title} <span className="text-purple-500">({meta.page_path})</span>
                              </h3>
                              <p className={`mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                <strong>Meta Title:</strong> {meta.meta_title}
                              </p>
                              <p className={`mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                <strong>Description:</strong> {meta.meta_description}
                              </p>
                              {meta.meta_keywords && (
                                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                  <strong>Keywords:</strong> {meta.meta_keywords}
                                </p>
                              )}
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={() => {
                                  setEditingMeta(meta);
                                  setNewMeta(meta);
                                  setShowMetaModal(true);
                                }}
                                className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
                              >
                                ✏️ Edit
                              </button>
                              <button
                                onClick={() => handleDeleteMetaTag(meta.id)}
                                className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
                              >
                                🗑️ Delete
                              </button>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}

              {/* Robots.txt Tab */}
              {activeTab === 'robots' && (
                <div>
                  <h2 className={`text-2xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    Robots.txt Editor
                  </h2>
                  <p className={`mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    Control how search engines crawl your site
                  </p>
                  
                  <textarea
                    value={robotsTxt}
                    onChange={(e) => setRobotsTxt(e.target.value)}
                    rows={15}
                    className={`w-full p-4 rounded-lg font-mono text-sm ${
                      isDarkMode 
                        ? 'bg-gray-900 text-gray-300 border-gray-700' 
                        : 'bg-white text-gray-900 border-gray-300'
                    } border-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent`}
                    placeholder="User-agent: *&#10;Allow: /"
                  />
                  
                  <button
                    onClick={handleSaveSeoSettings}
                    className="mt-4 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
                  >
                    💾 Save Robots.txt
                  </button>
                  
                  <div className={`mt-6 p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-blue-50'}`}>
                    <h3 className={`font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      💡 Tips:
                    </h3>
                    <ul className={`space-y-1 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      <li>• Use "Allow: /" to allow all crawlers</li>
                      <li>• Use "Disallow: /admin" to block admin pages</li>
                      <li>• Include your sitemap URL</li>
                      <li>• Test at google.com/search/docs/crawling-indexing/robots/intro</li>
                    </ul>
                  </div>
                </div>
              )}

              {/* Sitemap Tab */}
              {activeTab === 'sitemap' && (
                <div>
                  <h2 className={`text-2xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    Sitemap Settings
                  </h2>
                  
                  <div className="space-y-4">
                    <div className="flex items-center gap-4">
                      <label className={`flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        <input
                          type="checkbox"
                          checked={sitemapSettings.sitemap_enabled}
                          onChange={(e) => setSitemapSettings({...sitemapSettings, sitemap_enabled: e.target.checked})}
                          className="w-5 h-5"
                        />
                        <span>Enable Sitemap</span>
                      </label>
                    </div>
                    
                    <div>
                      <label className={`block mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        Homepage Priority (0.0 - 1.0)
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="1"
                        step="0.1"
                        value={sitemapSettings.sitemap_priority_home}
                        onChange={(e) => setSitemapSettings({...sitemapSettings, sitemap_priority_home: parseFloat(e.target.value)})}
                        className={`w-full p-3 rounded-lg ${
                          isDarkMode 
                            ? 'bg-gray-700 text-white border-gray-600' 
                            : 'bg-white text-gray-900 border-gray-300'
                        } border-2`}
                      />
                    </div>
                    
                    <div>
                      <label className={`block mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        Other Pages Priority (0.0 - 1.0)
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="1"
                        step="0.1"
                        value={sitemapSettings.sitemap_priority_pages}
                        onChange={(e) => setSitemapSettings({...sitemapSettings, sitemap_priority_pages: parseFloat(e.target.value)})}
                        className={`w-full p-3 rounded-lg ${
                          isDarkMode 
                            ? 'bg-gray-700 text-white border-gray-600' 
                            : 'bg-white text-gray-900 border-gray-300'
                        } border-2`}
                      />
                    </div>
                    
                    <div>
                      <label className={`block mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        Change Frequency
                      </label>
                      <select
                        value={sitemapSettings.sitemap_changefreq}
                        onChange={(e) => setSitemapSettings({...sitemapSettings, sitemap_changefreq: e.target.value})}
                        className={`w-full p-3 rounded-lg ${
                          isDarkMode 
                            ? 'bg-gray-700 text-white border-gray-600' 
                            : 'bg-white text-gray-900 border-gray-300'
                        } border-2`}
                      >
                        <option value="always">Always</option>
                        <option value="hourly">Hourly</option>
                        <option value="daily">Daily</option>
                        <option value="weekly">Weekly</option>
                        <option value="monthly">Monthly</option>
                        <option value="yearly">Yearly</option>
                        <option value="never">Never</option>
                      </select>
                    </div>
                  </div>
                  
                  <button
                    onClick={handleSaveSeoSettings}
                    className="mt-6 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
                  >
                    💾 Save Sitemap Settings
                  </button>
                  
                  <div className={`mt-6 p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-green-50'}`}>
                    <h3 className={`font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      🌐 Your Sitemap URL:
                    </h3>
                    <a
                      href={(sitemapPreview?.site_url || window.location.origin) + '/sitemap.xml'}
                      target="_blank"
                      rel="noreferrer"
                      className="text-purple-500 hover:underline break-all"
                    >
                      {(sitemapPreview?.site_url || window.location.origin) + '/sitemap.xml'}
                    </a>
                    <p className={`text-xs mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      On local dev (localhost:3000) the React server doesn't proxy /sitemap.xml — visit the backend directly at <code>http://localhost:8000/sitemap.xml</code> if needed. On live, this URL works via nginx.
                    </p>
                  </div>

                  {/* Sitemap Preview Panel */}
                  <div className={`mt-6 p-4 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-white border'}`}>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        📋 Pages in your sitemap {sitemapPreview ? `(${sitemapPreview.total} total)` : ''}
                      </h3>
                      <button
                        onClick={loadSitemapPreview}
                        disabled={sitemapLoading}
                        className="px-3 py-1.5 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
                      >
                        {sitemapLoading ? 'Loading...' : sitemapPreview ? '🔄 Refresh' : '📊 Show All Pages'}
                      </button>
                    </div>

                    {sitemapPreview && (
                      <>
                        <div className="flex flex-wrap gap-3 mb-3 text-xs">
                          <span className={`px-2 py-1 rounded ${isDarkMode ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-100 text-blue-700'}`}>
                            From Meta Tags: {sitemapPreview.by_source?.meta_tag || 0}
                          </span>
                          <span className={`px-2 py-1 rounded ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'}`}>
                            Default routes: {sitemapPreview.by_source?.default || 0}
                          </span>
                          {sitemapPreview.excluded_noindex?.length > 0 && (
                            <span className={`px-2 py-1 rounded ${isDarkMode ? 'bg-red-900/40 text-red-300' : 'bg-red-100 text-red-700'}`}>
                              Excluded (noindex): {sitemapPreview.excluded_noindex.length}
                            </span>
                          )}
                        </div>

                        <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
                          <table className="w-full text-sm">
                            <thead className={`sticky top-0 ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
                              <tr className={`border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                                <th className={`text-left p-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Path</th>
                                <th className={`text-left p-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Source</th>
                                <th className={`text-left p-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Priority</th>
                                <th className={`text-left p-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Change Freq</th>
                                <th className={`text-left p-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Last Modified</th>
                              </tr>
                            </thead>
                            <tbody>
                              {sitemapPreview.entries?.map((entry, i) => (
                                <tr key={i} className={`border-b ${isDarkMode ? 'border-gray-700 hover:bg-gray-700' : 'border-gray-100 hover:bg-gray-50'}`}>
                                  <td className={`p-2 font-mono text-xs ${isDarkMode ? 'text-purple-300' : 'text-purple-700'}`}>
                                    {entry.path}
                                    {entry.raw_path !== entry.path && (
                                      <span className={`ml-2 text-xs ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`} title={`Stored as ${entry.raw_path}, rewritten to ${entry.path}`}>
                                        ↻
                                      </span>
                                    )}
                                  </td>
                                  <td className="p-2">
                                    {entry.source === 'meta_tag' ? (
                                      <span className={`text-xs px-2 py-0.5 rounded ${isDarkMode ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-100 text-blue-700'}`}>meta tag</span>
                                    ) : (
                                      <span className={`text-xs px-2 py-0.5 rounded ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'}`}>default</span>
                                    )}
                                  </td>
                                  <td className={`p-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{entry.priority}</td>
                                  <td className={`p-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{entry.changefreq}</td>
                                  <td className={`p-2 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                    {entry.lastmod ? new Date(entry.lastmod).toLocaleDateString() : '—'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        {sitemapPreview.excluded_noindex?.length > 0 && (
                          <div className={`mt-3 p-3 rounded ${isDarkMode ? 'bg-red-900/20' : 'bg-red-50'}`}>
                            <p className={`text-xs font-bold mb-1 ${isDarkMode ? 'text-red-300' : 'text-red-700'}`}>
                              Excluded from sitemap (noindex robots tag):
                            </p>
                            <p className={`text-xs ${isDarkMode ? 'text-red-300' : 'text-red-700'}`}>
                              {sitemapPreview.excluded_noindex.map(e => e.path).join(', ')}
                            </p>
                          </div>
                        )}

                        <p className={`mt-3 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          💡 To add a new page: go to Meta Tags Manager → click "➕ Add Page Meta Tags". The dynamic sitemap picks it up automatically.
                        </p>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Social Media Tab */}
              {activeTab === 'social' && (
                <div>
                  <h2 className={`text-2xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    Social Media Links
                  </h2>
                  <p className={`mb-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    Add your social media profiles for better SEO and social sharing
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className={`block mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        📘 Facebook
                      </label>
                      <input
                        type="url"
                        value={socialLinks.facebook}
                        onChange={(e) => setSocialLinks({...socialLinks, facebook: e.target.value})}
                        placeholder="https://facebook.com/yourpage"
                        className={`w-full p-3 rounded-lg ${
                          isDarkMode 
                            ? 'bg-gray-700 text-white border-gray-600' 
                            : 'bg-white text-gray-900 border-gray-300'
                        } border-2`}
                      />
                    </div>
                    
                    <div>
                      <label className={`block mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        📷 Instagram
                      </label>
                      <input
                        type="url"
                        value={socialLinks.instagram}
                        onChange={(e) => setSocialLinks({...socialLinks, instagram: e.target.value})}
                        placeholder="https://instagram.com/yourpage"
                        className={`w-full p-3 rounded-lg ${
                          isDarkMode 
                            ? 'bg-gray-700 text-white border-gray-600' 
                            : 'bg-white text-gray-900 border-gray-300'
                        } border-2`}
                      />
                    </div>
                    
                    <div>
                      <label className={`block mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        🐦 Twitter / X
                      </label>
                      <input
                        type="url"
                        value={socialLinks.twitter}
                        onChange={(e) => setSocialLinks({...socialLinks, twitter: e.target.value})}
                        placeholder="https://twitter.com/yourpage"
                        className={`w-full p-3 rounded-lg ${
                          isDarkMode 
                            ? 'bg-gray-700 text-white border-gray-600' 
                            : 'bg-white text-gray-900 border-gray-300'
                        } border-2`}
                      />
                    </div>
                    
                    <div>
                      <label className={`block mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        💼 LinkedIn
                      </label>
                      <input
                        type="url"
                        value={socialLinks.linkedin}
                        onChange={(e) => setSocialLinks({...socialLinks, linkedin: e.target.value})}
                        placeholder="https://linkedin.com/company/yourpage"
                        className={`w-full p-3 rounded-lg ${
                          isDarkMode 
                            ? 'bg-gray-700 text-white border-gray-600' 
                            : 'bg-white text-gray-900 border-gray-300'
                        } border-2`}
                      />
                    </div>
                    
                    <div>
                      <label className={`block mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        🎵 TikTok
                      </label>
                      <input
                        type="url"
                        value={socialLinks.tiktok}
                        onChange={(e) => setSocialLinks({...socialLinks, tiktok: e.target.value})}
                        placeholder="https://tiktok.com/@yourpage"
                        className={`w-full p-3 rounded-lg ${
                          isDarkMode 
                            ? 'bg-gray-700 text-white border-gray-600' 
                            : 'bg-white text-gray-900 border-gray-300'
                        } border-2`}
                      />
                    </div>
                    
                    <div>
                      <label className={`block mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        📺 YouTube
                      </label>
                      <input
                        type="url"
                        value={socialLinks.youtube}
                        onChange={(e) => setSocialLinks({...socialLinks, youtube: e.target.value})}
                        placeholder="https://youtube.com/@yourpage"
                        className={`w-full p-3 rounded-lg ${
                          isDarkMode 
                            ? 'bg-gray-700 text-white border-gray-600' 
                            : 'bg-white text-gray-900 border-gray-300'
                        } border-2`}
                      />
                    </div>
                  </div>
                  
                  <button
                    onClick={handleSaveSeoSettings}
                    className="mt-6 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
                  >
                    💾 Save Social Media Links
                  </button>
                </div>
              )}

              {/* Redirects Tab */}
              {activeTab === 'redirects' && (
                <div>
                  <div className="flex justify-between items-center mb-6">
                    <div>
                      <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        301 Redirects Manager
                      </h2>
                      <p className={`mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        Redirect old URLs to new ones (SEO-friendly)
                      </p>
                    </div>
                    <button
                      onClick={() => setShowRedirectModal(true)}
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
                    >
                      ➕ Add Redirect
                    </button>
                  </div>
                  
                  <div className="space-y-4">
                    {redirects.length === 0 ? (
                      <div className={`text-center py-12 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        <div className="text-6xl mb-4">↩️</div>
                        <p className="text-xl mb-2">No redirects configured yet</p>
                        <p className="text-sm">Click "Add Redirect" to create your first one</p>
                      </div>
                    ) : (
                      redirects.map(redirect => (
                        <div
                          key={redirect.id}
                          className={`p-4 rounded-lg border-l-4 ${
                            redirect.enabled ? 'border-green-500' : 'border-gray-500'
                          } ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}
                        >
                          <div className="flex justify-between items-center">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <span className={`px-2 py-1 rounded text-xs font-bold ${
                                  redirect.enabled 
                                    ? 'bg-green-100 text-green-700' 
                                    : 'bg-gray-100 text-gray-700'
                                }`}>
                                  {redirect.enabled ? '✅ Active' : '⏸️ Disabled'}
                                </span>
                              </div>
                              <p className={`font-mono text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                <strong className="text-red-500">From:</strong> {redirect.from_url}
                              </p>
                              <p className={`font-mono text-sm mt-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                <strong className="text-green-500">To:</strong> {redirect.to_url}
                              </p>
                            </div>
                            <button
                              onClick={() => handleDeleteRedirect(redirect.id)}
                              className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-sm ml-4"
                            >
                              🗑️ Delete
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}

              {/* SEO Score Tab */}
              {activeTab === 'dashboard' && (
                <div>
                  <h2 className={`text-2xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    SEO Insights & Status
                  </h2>
                  
                  {/* SEO Score Card */}
                  <div className={`p-8 rounded-xl shadow-lg mb-6 ${
                    isDarkMode ? 'bg-gradient-to-br from-purple-900 to-indigo-900' : 'bg-gradient-to-br from-purple-50 to-indigo-50'
                  }`}>
                    <div className="text-center">
                      <h3 className={`text-2xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        🎯 Overall SEO Score
                      </h3>
                      <div className={`text-8xl font-bold mb-4 ${
                        seoScore.percentage >= 90 ? 'text-green-500' : 
                        seoScore.percentage >= 70 ? 'text-yellow-500' : 
                        seoScore.percentage >= 50 ? 'text-orange-500' :
                        'text-red-500'
                      }`}>
                        {seoScore.percentage}%
                      </div>
                      <p className={`text-xl ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        {seoScore.percentage >= 90 ? '🎉 Excellent! Your SEO is outstanding!' : 
                         seoScore.percentage >= 70 ? '👍 Good! Keep improving!' : 
                         seoScore.percentage >= 50 ? '⚠️ Fair - Needs attention' :
                         '❌ Poor - Urgent improvements needed'}
                      </p>
                      <p className={`text-sm mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        Score: {seoScore.score}/100 points
                      </p>
                    </div>
                  </div>

                  {/* Success & Issues */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    {/* Successes */}
                    <div className={`p-6 rounded-lg ${isDarkMode ? 'bg-green-900/30' : 'bg-green-50'}`}>
                      <h3 className={`text-lg font-bold mb-4 flex items-center gap-2 ${
                        isDarkMode ? 'text-green-300' : 'text-green-700'
                      }`}>
                        <span className="text-2xl">✅</span> What's Working ({seoScore.successes.length})
                      </h3>
                      <ul className={`space-y-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        {seoScore.successes.map((success, idx) => (
                          <li key={idx} className="text-sm">{success}</li>
                        ))}
                      </ul>
                    </div>

                    {/* Issues */}
                    <div className={`p-6 rounded-lg ${isDarkMode ? 'bg-yellow-900/30' : 'bg-yellow-50'}`}>
                      <h3 className={`text-lg font-bold mb-4 flex items-center gap-2 ${
                        isDarkMode ? 'text-yellow-300' : 'text-yellow-700'
                      }`}>
                        <span className="text-2xl">⚠️</span> Improvements Needed ({seoScore.issues.length})
                      </h3>
                      <ul className={`space-y-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        {seoScore.issues.length > 0 ? (
                          seoScore.issues.map((issue, idx) => (
                            <li key={idx} className="text-sm">{issue}</li>
                          ))
                        ) : (
                          <li className="text-sm text-green-600">🎉 No issues found! Perfect score!</li>
                        )}
                      </ul>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                    <div className={`p-6 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-blue-50'}`}>
                      <div className="text-3xl mb-2">🏷️</div>
                      <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {metaTags.length}
                      </h3>
                      <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        Meta Tags Configured
                      </p>
                      <div className={`mt-2 px-2 py-1 rounded text-xs font-bold inline-block ${
                        metaTags.length > 0 ? 'bg-green-500 text-white' : 'bg-gray-500 text-white'
                      }`}>
                        {metaTags.length > 0 ? '✅ Active' : '⏸️ Setup Needed'}
                      </div>
                    </div>
                    
                    <div className={`p-6 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-purple-50'}`}>
                      <div className="text-3xl mb-2">🤖</div>
                      <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        Robots.txt
                      </h3>
                      <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        Search Engine Control
                      </p>
                      <div className="mt-2 px-2 py-1 rounded text-xs font-bold inline-block bg-green-500 text-white">
                        ✅ Active
                      </div>
                    </div>
                    
                    <div className={`p-6 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-green-50'}`}>
                      <div className="text-3xl mb-2">🗺️</div>
                      <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        Sitemap
                      </h3>
                      <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        XML Sitemap
                      </p>
                      <div className={`mt-2 px-2 py-1 rounded text-xs font-bold inline-block ${
                        sitemapSettings.sitemap_enabled ? 'bg-green-500 text-white' : 'bg-gray-500 text-white'
                      }`}>
                        {sitemapSettings.sitemap_enabled ? '✅ Active' : '⏸️ Disabled'}
                      </div>
                    </div>
                    
                    <div className={`p-6 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-pink-50'}`}>
                      <div className="text-3xl mb-2">📱</div>
                      <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {Object.values(socialLinks).filter(link => link).length}
                      </h3>
                      <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        Social Media Links
                      </p>
                      <div className={`mt-2 px-2 py-1 rounded text-xs font-bold inline-block ${
                        Object.values(socialLinks).filter(link => link).length > 0 ? 'bg-green-500 text-white' : 'bg-gray-500 text-white'
                      }`}>
                        {Object.values(socialLinks).filter(link => link).length > 0 ? '✅ Active' : '⏸️ Setup Needed'}
                      </div>
                    </div>
                    
                    <div className={`p-6 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-yellow-50'}`}>
                      <div className="text-3xl mb-2">↩️</div>
                      <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {redirects.length}
                      </h3>
                      <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        301 Redirects
                      </p>
                      <div className={`mt-2 px-2 py-1 rounded text-xs font-bold inline-block ${
                        redirects.length > 0 ? 'bg-green-500 text-white' : 'bg-gray-500 text-white'
                      }`}>
                        {redirects.length > 0 ? '✅ Active' : '⏸️ None'}
                      </div>
                    </div>
                    
                    <div className={`p-6 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-indigo-50'}`}>
                      <div className="text-3xl mb-2">🎯</div>
                      <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        Overall
                      </h3>
                      <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        SEO Health
                      </p>
                      <div className="mt-2 px-2 py-1 rounded text-xs font-bold inline-block bg-green-500 text-white">
                        ✅ Good
                      </div>
                    </div>
                  </div>
                  
                  <div className={`p-6 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-blue-50'}`}>
                    <h3 className={`text-lg font-bold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      💡 Quick Tips
                    </h3>
                    <ul className={`space-y-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      <li>✅ Configure meta tags for all important pages</li>
                      <li>✅ Keep meta descriptions under 160 characters</li>
                      <li>✅ Use unique titles for each page</li>
                      <li>✅ Submit your sitemap to Google Search Console</li>
                      <li>✅ Set up 301 redirects for any changed URLs</li>
                      <li>✅ Link all active social media profiles</li>
                    </ul>
                  </div>
                </div>
              )}

              {/* Links Management Tab */}
              {activeTab === 'links' && (
                <div>
                  <h2 className={`text-2xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    🔗 Links Management
                  </h2>
                  <p className={`mb-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    Control how links behave on your website for better SEO and user experience
                  </p>
                  
                  <div className="space-y-6">
                    {/* External Links */}
                    <div className={`p-6 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}>
                      <h3 className={`text-lg font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        🌐 External Links
                      </h3>
                      
                      <div className="space-y-4">
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={linkSettings.nofollow_external_links}
                            onChange={(e) => setLinkSettings({...linkSettings, nofollow_external_links: e.target.checked})}
                            className="w-5 h-5"
                          />
                          <div>
                            <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                              Add rel="nofollow" to External Links
                            </span>
                            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                              Prevents search engines from following external links, preserving your site's link juice
                            </p>
                          </div>
                        </label>
                        
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={linkSettings.open_external_new_tab}
                            onChange={(e) => setLinkSettings({...linkSettings, open_external_new_tab: e.target.checked})}
                            className="w-5 h-5"
                          />
                          <div>
                            <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                              Open External Links in New Tab/Window
                            </span>
                            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                              Keeps users on your site by opening external links in new tabs
                            </p>
                          </div>
                        </label>
                      </div>
                    </div>
                    
                    {/* Attachment Links */}
                    <div className={`p-6 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}>
                      <h3 className={`text-lg font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        📎 Attachment Redirects
                      </h3>
                      
                      <div className="space-y-4">
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={linkSettings.redirect_attachments}
                            onChange={(e) => setLinkSettings({...linkSettings, redirect_attachments: e.target.checked})}
                            className="w-5 h-5"
                          />
                          <div>
                            <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                              Redirect Attachments to Parent Post
                            </span>
                            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                              Automatically redirect image/file attachment pages to the post they belong to
                            </p>
                          </div>
                        </label>
                        
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={linkSettings.redirect_orphan_attachments}
                            onChange={(e) => setLinkSettings({...linkSettings, redirect_orphan_attachments: e.target.checked})}
                            className="w-5 h-5"
                          />
                          <div>
                            <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                              Redirect Orphan Attachments to Homepage
                            </span>
                            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                              Redirect attachments without a parent post to your homepage
                            </p>
                          </div>
                        </label>
                      </div>
                    </div>
                    
                    {/* Image File Links */}
                    <div className={`p-6 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}>
                      <h3 className={`text-lg font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        🖼️ Image File Links
                      </h3>
                      
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={linkSettings.nofollow_image_links}
                          onChange={(e) => setLinkSettings({...linkSettings, nofollow_image_links: e.target.checked})}
                          className="w-5 h-5"
                        />
                        <div>
                          <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            Add rel="nofollow" to Image File Links
                          </span>
                          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            Prevents search engines from indexing direct image file URLs
                          </p>
                        </div>
                      </label>
                    </div>
                  </div>
                  
                  <button
                    onClick={handleSaveSeoSettings}
                    className="mt-6 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
                  >
                    💾 Save Link Settings
                  </button>
                </div>
              )}

              {/* Images SEO Tab */}
              {activeTab === 'images' && (
                <div>
                  <h2 className={`text-2xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    🖼️ Image SEO Optimization
                  </h2>
                  <p className={`mb-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    Automatically optimize images for better SEO and accessibility
                  </p>
                  
                  <div className="space-y-6">
                    {/* ALT Attributes */}
                    <div className={`p-6 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}>
                      <h3 className={`text-lg font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        🏷️ ALT Attributes
                      </h3>
                      
                      <div className="space-y-4">
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={imageSettings.add_missing_alt}
                            onChange={(e) => setImageSettings({...imageSettings, add_missing_alt: e.target.checked})}
                            className="w-5 h-5"
                          />
                          <div>
                            <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                              Add Missing ALT Attributes
                            </span>
                            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                              Automatically add ALT text to images missing this crucial SEO element
                            </p>
                          </div>
                        </label>
                        
                        <div>
                          <label className={`block mb-2 font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            ALT Attribute Format
                          </label>
                          <input
                            type="text"
                            value={imageSettings.alt_format}
                            onChange={(e) => setImageSettings({...imageSettings, alt_format: e.target.value})}
                            placeholder="%title% - %sitename%"
                            className={`w-full p-3 rounded-lg ${
                              isDarkMode 
                                ? 'bg-gray-800 text-white border-gray-600' 
                                : 'bg-white text-gray-900 border-gray-300'
                            } border-2`}
                          />
                          <p className={`text-xs mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            Variables: %title%, %sitename%, %filename%, %caption%
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    {/* TITLE Attributes */}
                    <div className={`p-6 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}>
                      <h3 className={`text-lg font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        📝 TITLE Attributes
                      </h3>
                      
                      <div className="space-y-4">
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={imageSettings.add_missing_title}
                            onChange={(e) => setImageSettings({...imageSettings, add_missing_title: e.target.checked})}
                            className="w-5 h-5"
                          />
                          <div>
                            <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                              Add Missing TITLE Attributes
                            </span>
                            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                              Add title attributes to images for better user experience and SEO
                            </p>
                          </div>
                        </label>
                        
                        <div>
                          <label className={`block mb-2 font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            TITLE Attribute Format
                          </label>
                          <input
                            type="text"
                            value={imageSettings.title_format}
                            onChange={(e) => setImageSettings({...imageSettings, title_format: e.target.value})}
                            placeholder="%title%"
                            className={`w-full p-3 rounded-lg ${
                              isDarkMode 
                                ? 'bg-gray-800 text-white border-gray-600' 
                                : 'bg-white text-gray-900 border-gray-300'
                            } border-2`}
                          />
                          <p className={`text-xs mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            Variables: %title%, %sitename%, %filename%, %caption%
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Image SEO Tips */}
                    <div className={`p-6 rounded-lg ${isDarkMode ? 'bg-blue-900/30' : 'bg-blue-50'}`}>
                      <h3 className={`text-lg font-bold mb-3 ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                        💡 Image SEO Best Practices
                      </h3>
                      <ul className={`space-y-2 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        <li>✅ Use descriptive, keyword-rich ALT text</li>
                        <li>✅ Keep ALT text concise (125 characters or less)</li>
                        <li>✅ Include target keywords naturally</li>
                        <li>✅ Describe what's in the image accurately</li>
                        <li>✅ Don't stuff keywords - write for humans</li>
                        <li>✅ Use relevant file names before uploading</li>
                      </ul>
                    </div>
                  </div>
                  
                  <button
                    onClick={handleSaveSeoSettings}
                    className="mt-6 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
                  >
                    💾 Save Image Settings
                  </button>
                </div>
              )}

              {/* 404 Monitor Tab */}
              {activeTab === 'monitor' && (
                <div>
                  <h2 className={`text-2xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    🚨 404 Error Monitor
                  </h2>
                  <p className={`mb-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    Track broken pages that ruin user experience and affect SEO
                  </p>
                  
                  <div className="space-y-6">
                    {/* Monitor Settings */}
                    <div className={`p-6 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}>
                      <h3 className={`text-lg font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        ⚙️ Monitor Settings
                      </h3>
                      
                      <div className="space-y-4">
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={monitor404.enabled}
                            onChange={(e) => setMonitor404({...monitor404, enabled: e.target.checked})}
                            className="w-5 h-5"
                          />
                          <div>
                            <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                              Enable 404 Error Monitoring
                            </span>
                            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                              Track all 404 errors on your website
                            </p>
                          </div>
                        </label>
                        
                        <div>
                          <label className={`block mb-2 font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            Monitoring Mode
                          </label>
                          <select
                            value={monitor404.mode}
                            onChange={(e) => setMonitor404({...monitor404, mode: e.target.value})}
                            className={`w-full p-3 rounded-lg ${
                              isDarkMode 
                                ? 'bg-gray-800 text-white border-gray-600' 
                                : 'bg-white text-gray-900 border-gray-300'
                            } border-2`}
                          >
                            <option value="simple">Simple Mode - URI and Access Time Only</option>
                            <option value="advanced">Advanced Mode - Detailed Logs with Referer, User-Agent, etc.</option>
                          </select>
                          <p className={`text-xs mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            Advanced mode provides more debugging information but uses more storage
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    {/* 404 Errors Log */}
                    <div className={`p-6 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}>
                      <div className="flex justify-between items-center mb-4">
                        <h3 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          📋 Recent 404 Errors
                        </h3>
                        <div className="flex items-center gap-3">
                          <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            {monitor404.logs.length} errors logged
                          </span>
                          {monitor404.logs.length > 0 && (
                            <button
                              onClick={() => setClear404ModalOpen(true)}
                              className="px-3 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700"
                            >
                              🗑️ Clear All
                            </button>
                          )}
                        </div>
                      </div>
                      
                      {monitor404.logs.length === 0 ? (
                        <div className={`text-center py-12 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          <div className="text-6xl mb-4">✅</div>
                          <p className="text-xl mb-2">No 404 Errors Found</p>
                          <p className="text-sm">Your website is error-free! Great job! 🎉</p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {monitor404.logs.map((log, idx) => (
                            <div
                              key={idx}
                              className={`p-4 rounded-lg border-l-4 border-red-500 ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}
                            >
                              <div className="flex justify-between items-start mb-2">
                                <div className="flex-1">
                                  <p className={`text-xs font-bold mb-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                    404 LINK:
                                  </p>
                                  <a 
                                    href={log.fullUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={`font-mono text-sm break-all hover:underline ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}
                                  >
                                    {log.fullUrl || `${window.location.origin}${log.uri}`}
                                  </a>
                                </div>
                                <div className="flex items-center gap-2 ml-2">
                                  <span className={`px-2 py-1 rounded text-xs font-bold ${
                                    log.hits > 10 ? 'bg-red-500 text-white' : 'bg-yellow-500 text-white'
                                  }`}>
                                    {log.hits} hits
                                  </span>
                                  <button
                                    onClick={() => setDelete404Target(log)}
                                    title="Delete this 404 entry"
                                    className={`p-1.5 rounded text-xs ${isDarkMode ? 'bg-red-900/40 text-red-300 hover:bg-red-900/60' : 'bg-red-50 text-red-700 hover:bg-red-100'}`}
                                  >
                                    🗑️
                                  </button>
                                </div>
                              </div>
                              {monitor404.mode === 'advanced' && (
                                <div className={`text-xs mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                  <p>📍 Referer: {log.referer || 'Direct'}</p>
                                  <p>🕒 Last seen: {log.lastSeen}</p>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    {/* 404 Actions */}
                    <div className={`p-6 rounded-lg ${isDarkMode ? 'bg-yellow-900/30' : 'bg-yellow-50'}`}>
                      <h3 className={`text-lg font-bold mb-3 ${isDarkMode ? 'text-yellow-300' : 'text-yellow-700'}`}>
                        💡 What to Do About 404 Errors
                      </h3>
                      <ul className={`space-y-2 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        <li>✅ Create content for frequently requested URLs</li>
                        <li>✅ Set up 301 redirects to relevant existing pages</li>
                        <li>✅ Fix broken internal links on your site</li>
                        <li>✅ Update external links pointing to old URLs</li>
                        <li>✅ Check Google Search Console for more errors</li>
                        <li>✅ Update sitemap to reflect current structure</li>
                      </ul>
                    </div>
                  </div>
                  
                  <button
                    onClick={handleSaveSeoSettings}
                    className="mt-6 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
                  >
                    💾 Save Monitor Settings
                  </button>
                </div>
              )}

              {/* Google Analytics Tab */}
              {activeTab === 'analytics' && (
                <div>
                  <h2 className={`text-2xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    📊 Google Analytics Integration
                  </h2>
                  <p className={`mb-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    Connect Google Analytics to track your website traffic and SEO performance
                  </p>
                  
                  <div className="space-y-6">
                    {/* Setup */}
                    <div className={`p-6 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}>
                      <h3 className={`text-lg font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        🔧 Setup Google Analytics
                      </h3>
                      
                      <div className="space-y-4">
                        <div>
                          <label className={`block mb-2 font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            Google Analytics Tracking ID / Measurement ID
                          </label>
                          <input
                            type="text"
                            value={seoSettings?.google_analytics_id || ''}
                            onChange={(e) => setSeoSettings({...seoSettings, google_analytics_id: e.target.value})}
                            placeholder="G-XXXXXXXXXX or UA-XXXXXXXXX-X"
                            className={`w-full p-3 rounded-lg font-mono ${
                              isDarkMode 
                                ? 'bg-gray-800 text-white border-gray-600' 
                                : 'bg-white text-gray-900 border-gray-300'
                            } border-2`}
                          />
                          <p className={`text-xs mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            Example: G-ABC123DEF4 (GA4) or UA-123456789-1 (Universal Analytics)
                          </p>
                        </div>
                        
                        <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-blue-900/30' : 'bg-blue-50'}`}>
                          <h4 className={`font-bold mb-2 ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                            🔍 Where to Find Your Tracking ID:
                          </h4>
                          <ol className={`text-sm space-y-1 list-decimal list-inside ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            <li>Go to <a href="https://analytics.google.com" target="_blank" rel="noopener noreferrer" className="text-blue-500 underline">analytics.google.com</a></li>
                            <li>Click "Admin" (gear icon) at bottom left</li>
                            <li>Select your property → "Data Streams"</li>
                            <li>Click your web stream</li>
                            <li>Copy the "Measurement ID" (starts with G-)</li>
                          </ol>
                        </div>
                      </div>
                    </div>
                    
                    {/* Stats Dashboard */}
                    <div className={`p-6 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}>
                      <h3 className={`text-lg font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        📈 Analytics Dashboard
                      </h3>
                      
                      {seoSettings?.google_analytics_id ? (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div className={`p-4 rounded-lg text-center ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
                            <div className="text-3xl font-bold text-purple-500 mb-2">-</div>
                            <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                              Page Views (7 days)
                            </div>
                          </div>
                          
                          <div className={`p-4 rounded-lg text-center ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
                            <div className="text-3xl font-bold text-green-500 mb-2">-</div>
                            <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                              Unique Visitors
                            </div>
                          </div>
                          
                          <div className={`p-4 rounded-lg text-center ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
                            <div className="text-3xl font-bold text-blue-500 mb-2">-</div>
                            <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                              Avg. Session Duration
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          <div className="text-6xl mb-4">📊</div>
                          <p className="text-lg mb-2">No Analytics Connected</p>
                          <p className="text-sm">Add your Tracking ID above to see stats</p>
                        </div>
                      )}
                      
                      {seoSettings?.google_analytics_id && (
                        <div className="mt-4 text-center">
                          <a 
                            href="https://analytics.google.com" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="inline-block px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                          >
                            🔗 Open Full Google Analytics Dashboard
                          </a>
                        </div>
                      )}
                    </div>
                    
                    {/* Search Console */}
                    <div className={`p-6 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}>
                      <h3 className={`text-lg font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        🔍 Google Search Console
                      </h3>
                      
                      <div className="space-y-4">
                        <div>
                          <label className={`block mb-2 font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            Verification Code (Optional)
                          </label>
                          <input
                            type="text"
                            value={seoSettings?.google_search_console_verification || ''}
                            onChange={(e) => setSeoSettings({...seoSettings, google_search_console_verification: e.target.value})}
                            placeholder="google1234567890abcdef.html or meta tag content"
                            className={`w-full p-3 rounded-lg font-mono ${
                              isDarkMode 
                                ? 'bg-gray-800 text-white border-gray-600' 
                                : 'bg-white text-gray-900 border-gray-300'
                            } border-2`}
                          />
                        </div>
                        
                        <a 
                          href="https://search.google.com/search-console" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="inline-block px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm"
                        >
                          🔗 Open Google Search Console
                        </a>
                      </div>
                    </div>
                  </div>
                  
                  <button
                    onClick={handleSaveSeoSettings}
                    className="mt-6 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
                  >
                    💾 Save Analytics Settings
                  </button>
                </div>
              )}

              {/* Security Tab */}
              {activeTab === 'security' && (
                <div>
                  <h2 className={`text-2xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    🔒 Security & Protection
                  </h2>
                  <p className={`mb-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    Advanced security features to protect your website
                  </p>
                  
                  <div className="space-y-6">
                    {/* Active Security Features */}
                    <div className={`p-6 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}>
                      <h3 className={`text-lg font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        🛡️ Active Security Headers
                      </h3>
                      
                      <div className="space-y-4">
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={securitySettings.xframe_options}
                            onChange={(e) => setSecuritySettings({...securitySettings, xframe_options: e.target.checked})}
                            className="w-5 h-5"
                          />
                          <div className="flex-1">
                            <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                              X-Frame-Options (Clickjacking Protection)
                            </span>
                            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                              Prevents your site from being embedded in iframes on other domains
                            </p>
                          </div>
                          <span className={`px-3 py-1 rounded text-xs font-bold ${
                            securitySettings.xframe_options ? 'bg-green-500 text-white' : 'bg-gray-500 text-white'
                          }`}>
                            {securitySettings.xframe_options ? '✅ Active' : '⏸️ Disabled'}
                          </span>
                        </label>
                        
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={securitySettings.xss_protection}
                            onChange={(e) => setSecuritySettings({...securitySettings, xss_protection: e.target.checked})}
                            className="w-5 h-5"
                          />
                          <div className="flex-1">
                            <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                              XSS Protection (Cross-Site Scripting)
                            </span>
                            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                              Protects against XSS attacks by filtering malicious scripts
                            </p>
                          </div>
                          <span className={`px-3 py-1 rounded text-xs font-bold ${
                            securitySettings.xss_protection ? 'bg-green-500 text-white' : 'bg-gray-500 text-white'
                          }`}>
                            {securitySettings.xss_protection ? '✅ Active' : '⏸️ Disabled'}
                          </span>
                        </label>
                        
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={securitySettings.hsts_enabled}
                            onChange={(e) => setSecuritySettings({...securitySettings, hsts_enabled: e.target.checked})}
                            className="w-5 h-5"
                          />
                          <div className="flex-1">
                            <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                              HSTS Enable (HTTP Strict Transport Security)
                            </span>
                            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                              Forces HTTPS connections, prevents SSL stripping attacks
                            </p>
                          </div>
                          <span className={`px-3 py-1 rounded text-xs font-bold ${
                            securitySettings.hsts_enabled ? 'bg-green-500 text-white' : 'bg-gray-500 text-white'
                          }`}>
                            {securitySettings.hsts_enabled ? '✅ Active' : '⏸️ Disabled'}
                          </span>
                        </label>
                      </div>
                    </div>
                    
                    {/* Content Security Policy (CSP) — with STOP warning INSIDE the same card */}
                    <div className={`p-6 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}>
                      <h3 className={`text-lg font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        Content Security Policy (CSP)
                      </h3>

                      {/* STOP banner — inside the card, above the toggle */}
                      <div className={`p-4 rounded-lg border-2 mb-4 ${isDarkMode ? 'bg-red-900/30 border-red-700' : 'bg-red-50 border-red-300'}`}>
                        <div className="flex items-start gap-3">
                          <span className="text-2xl flex-shrink-0">{'⚠️'}</span>
                          <div className="flex-1">
                            <h4 className={`font-bold mb-2 ${isDarkMode ? 'text-red-300' : 'text-red-800'}`}>
                              STOP — Read before enabling Content Security Policy
                            </h4>
                            <p className={`text-sm mb-2 ${isDarkMode ? 'text-red-200' : 'text-red-700'}`}>
                              CSP is the strictest header. Enabling it <strong>as-is</strong> on darijapro.com will silently break several real things:
                            </p>
                            <ul className={`text-sm space-y-1 mb-3 list-none ${isDarkMode ? 'text-red-200' : 'text-red-700'}`}>
                              <li>{'❌'} <strong>Stripe checkout</strong> — needs <code>https://js.stripe.com</code> in <code>script-src</code></li>
                              <li>{'❌'} <strong>Google Analytics</strong> — needs <code>https://www.googletagmanager.com</code> and <code>https://www.google-analytics.com</code></li>
                              <li>{'❌'} <strong>AI provider API calls from frontend</strong> — backend handles them, so probably OK, but verify</li>
                              <li>{'❌'} <strong>Any third-party fonts beyond Google Fonts</strong> — must be added to <code>font-src</code></li>
                              <li>{'❌'} <strong>Embedded YouTube / iframes</strong> — need a <code>frame-src</code> directive</li>
                            </ul>
                            <p className={`text-sm font-medium ${isDarkMode ? 'text-red-200' : 'text-red-800'}`}>
                              {'✅'} Leave CSP <strong>DISABLED</strong> until you have audited every third-party domain your site uses and tuned the directives below for your exact setup. Misconfigured CSP = white-screen-of-death for real users.
                            </p>
                          </div>
                        </div>
                      </div>

                      <label className="flex items-center gap-3 cursor-pointer mb-4">
                        <input
                          type="checkbox"
                          checked={securitySettings.csp_enabled}
                          onChange={(e) => setSecuritySettings({...securitySettings, csp_enabled: e.target.checked})}
                          className="w-5 h-5"
                        />
                        <div className="flex-1">
                          <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            Enable Content Security Policy
                          </span>
                          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            Controls which resources the browser is allowed to load. Prevents XSS and data injection attacks.
                          </p>
                        </div>
                        <span className={`px-3 py-1 rounded text-xs font-bold ${
                          securitySettings.csp_enabled ? 'bg-green-500 text-white' : 'bg-gray-500 text-white'
                        }`}>
                          {securitySettings.csp_enabled ? 'Active' : 'Disabled'}
                        </span>
                      </label>

                      {securitySettings.csp_enabled && (
                        <div className="mt-3">
                          <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            CSP Directives
                          </label>
                          <textarea
                            value={securitySettings.csp_directives}
                            onChange={(e) => setSecuritySettings({...securitySettings, csp_directives: e.target.value})}
                            rows={4}
                            className={`w-full p-3 rounded-lg border font-mono text-sm ${
                              isDarkMode ? 'bg-gray-800 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                            }`}
                            placeholder="default-src 'self'; script-src 'self' 'unsafe-inline'..."
                          />
                          <p className={`text-xs mt-1 ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>
                            Be careful: incorrect CSP directives can break your site. Test changes thoroughly.
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Server Protection Info */}
                    <div className={`p-6 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}>
                      <h3 className={`text-lg font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        Server Protection
                      </h3>
                      <div className="space-y-3">
                        <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
                          <div className="flex items-center gap-2">
                            <span className="px-2 py-0.5 rounded text-xs font-bold bg-green-500 text-white">Always On</span>
                            <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Directory Listing Prevention</span>
                          </div>
                          <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>FastAPI does not expose directory listings by default</p>
                        </div>
                        <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
                          <div className="flex items-center gap-2">
                            <span className="px-2 py-0.5 rounded text-xs font-bold bg-green-500 text-white">Always On</span>
                            <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Content-Type-Options (nosniff)</span>
                          </div>
                          <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Prevents MIME type sniffing attacks</p>
                        </div>
                        <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
                          <div className="flex items-center gap-2">
                            <span className="px-2 py-0.5 rounded text-xs font-bold bg-green-500 text-white">Always On</span>
                            <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Referrer-Policy (strict-origin-when-cross-origin)</span>
                          </div>
                          <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Controls how much referrer information is sent</p>
                        </div>
                        <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
                          <div className="flex items-center gap-2">
                            <span className="px-2 py-0.5 rounded text-xs font-bold bg-green-500 text-white">Always On</span>
                            <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>API & Admin Route Protection</span>
                          </div>
                          <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>All API routes require JWT authentication. Admin routes require admin credentials.</p>
                        </div>
                      </div>
                    </div>

                    {/* Security Score */}
                    <div className={`p-6 rounded-lg ${isDarkMode ? 'bg-gradient-to-br from-purple-900 to-indigo-900' : 'bg-gradient-to-br from-purple-50 to-indigo-50'}`}>
                      <h3 className={`text-xl font-bold mb-4 text-center ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        Security Score
                      </h3>
                      <div className="text-center">
                        {(() => {
                          const features = [securitySettings.xframe_options, securitySettings.xss_protection, securitySettings.hsts_enabled, securitySettings.csp_enabled];
                          const enabled = features.filter(Boolean).length;
                          const total = features.length;
                          const baseScore = 60; // always-on features (nosniff, referrer, directory, route protection)
                          const toggleScore = Math.round((enabled / total) * 40);
                          const score = baseScore + toggleScore;
                          return (
                            <>
                              <div className={`text-6xl font-bold mb-2 ${score === 100 ? 'text-green-500' : score >= 80 ? 'text-blue-500' : 'text-yellow-500'}`}>
                                {score}%
                              </div>
                              <p className={`text-lg ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                {score === 100 ? 'Excellent Security!' : score >= 80 ? 'Good Security' : 'Enable more features for better security'}
                              </p>
                              <p className={`text-sm mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                {enabled}/{total} configurable headers active + 4 always-on protections
                              </p>
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                  
                  <button
                    onClick={handleSaveSeoSettings}
                    className="mt-6 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
                  >
                    💾 Save Security Settings
                  </button>
                </div>
              )}

              {/* AI Tools Tab */}
              {/* Schema Markup Tab */}
              {activeTab === 'schema' && (
                <SchemaMarkupTab isDarkMode={isDarkMode} getAuthToken={getAuthToken} showNotification={showNotification} metaTags={metaTags} loadSEOData={loadSEOData} />
              )}

              {/* SEO Tools Tab */}
              {activeTab === 'seo-tools' && (
                <SEOToolsTab isDarkMode={isDarkMode} getAuthToken={getAuthToken} showNotification={showNotification} metaTags={metaTags} loadSEOData={loadSEOData} />
              )}

              {/* API Providers Tab */}
              {activeTab === 'api-providers' && (
                <APIProvidersTab isDarkMode={isDarkMode} getAuthToken={getAuthToken} showNotification={showNotification} />
              )}

              {/* REMOVED: old ai-tools tab replaced above */}
              {activeTab === '__removed_ai_tools__' && (
                <div>
                  <h2 className={`text-2xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    Removed
                  </h2>
                  <p className={`mb-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    Use AI to automatically optimize your SEO to 100%
                  </p>
                  
                  <div className="space-y-6">
                    {/* AI Meta Generator */}
                    <div className={`p-6 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}>
                      <h3 className={`text-lg font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        ✨ AI Meta Tag Generator
                      </h3>
                      
                      <p className={`mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        Automatically generate optimized meta tags for all your pages using AI
                      </p>
                      
                      <div className={`p-4 rounded-lg mb-4 ${isDarkMode ? 'bg-blue-900/30' : 'bg-blue-50'}`}>
                        <h4 className={`font-bold mb-2 ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                          What AI Will Do:
                        </h4>
                        <ul className={`text-sm space-y-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          <li>✅ Optimize title length (30-60 characters)</li>
                          <li>✅ Optimize description length (120-160 characters)</li>
                          <li>✅ Add relevant keywords naturally</li>
                          <li>✅ Generate compelling Open Graph tags</li>
                          <li>✅ Create Twitter Card optimization</li>
                          <li>✅ Ensure uniqueness across all pages</li>
                          <li>✅ Follow SEO best practices</li>
                        </ul>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                        <div className={`p-4 rounded-lg text-center ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
                          <div className="text-3xl font-bold text-purple-500 mb-2">{metaTags.length}</div>
                          <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            Pages to Optimize
                          </div>
                        </div>
                        
                        <div className={`p-4 rounded-lg text-center ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
                          <div className="text-3xl font-bold text-green-500 mb-2">~2min</div>
                          <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            Processing Time
                          </div>
                        </div>
                        
                        <div className={`p-4 rounded-lg text-center ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
                          <div className="text-3xl font-bold text-blue-500 mb-2">100%</div>
                          <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            Target Score
                          </div>
                        </div>
                      </div>
                      
                      <button
                        onClick={async () => {
                          setAiGenerating(true);
                          try {
                            const token = getAuthToken();
                            const backendUrl = process.env.REACT_APP_BACKEND_URL;
                            
                            // Show progress
                            showNotification('🔍 AI is scanning your website...', 'success');
                            await new Promise(resolve => setTimeout(resolve, 1000));
                            
                            // STEP 1: Fill Robots.txt
                            showNotification('🤖 Creating optimized robots.txt...', 'success');
                            const optimizedRobotsTxt = `# Robots.txt for Darija Pro - Learn Moroccan Arabic
# Optimized by AI on ${new Date().toLocaleDateString()}

User-agent: *
Allow: /
Allow: /translation
Allow: /games
Allow: /daily-words
Allow: /categories
Allow: /membership

# Block admin and sensitive areas
Disallow: /admin/
Disallow: /${process.env.REACT_APP_ADMIN_ROUTE || 'admin-panel'}
Disallow: /api/
Disallow: /backend/

# Block authentication
Disallow: /login
Disallow: /register
Disallow: /*?token=
Disallow: /*?admin=

# Sitemap
Sitemap: ${window.location.origin}/sitemap.xml

# Crawl delay
Crawl-delay: 1`;
                            setRobotsTxt(optimizedRobotsTxt);
                            await new Promise(resolve => setTimeout(resolve, 1500));
                            
                            // STEP 2: Create/Update Meta Tags
                            showNotification('📝 Creating SEO-optimized meta tags...', 'success');
                            const pages = [
                              {
                                page_path: '/',
                                page_title: 'Home',
                                meta_title: 'Darija Pro - Learn Moroccan Arabic Online Fast & Easy',
                                meta_description: 'Master Moroccan Darija with interactive games, instant translation, and daily lessons. Join thousands learning Arabic dialect. Free tools + PRO features!',
                                meta_keywords: 'learn darija, moroccan arabic, darija translation, moroccan dialect, arabic games'
                              },
                              {
                                page_path: '/translation',
                                page_title: 'Translation',
                                meta_title: 'Free Darija Translator - English to Moroccan Arabic',
                                meta_description: 'Instant translation between English and Moroccan Darija. Accurate, fast, and free. Translate words, phrases, and sentences with pronunciation guide.',
                                meta_keywords: 'darija translator, english to darija, moroccan arabic translation'
                              },
                              {
                                page_path: '/games',
                                page_title: 'Games',
                                meta_title: 'Darija Learning Games - Fun Moroccan Arabic Practice',
                                meta_description: 'Learn Moroccan Darija through interactive games. Word challenges, conversation practice, and vocabulary building. Free and PRO games available now!',
                                meta_keywords: 'darija games, moroccan arabic games, learn darija games'
                              },
                              {
                                page_path: '/daily-words',
                                page_title: 'Daily Words',
                                meta_title: 'Daily Darija Lessons - Learn 3 New Words Every Day',
                                meta_description: 'Master Moroccan Darija with daily word lessons. Spaced repetition, pronunciation practice, and progress tracking. Start your daily learning habit!',
                                meta_keywords: 'daily darija lessons, learn darija daily, moroccan vocabulary'
                              },
                              {
                                page_path: '/membership',
                                page_title: 'Membership',
                                meta_title: 'Go PRO - Unlimited Darija Learning + Premium Features',
                                meta_description: 'Upgrade to Darija Pro: unlimited translations, exclusive games, advanced conversation practice, ad-free learning. 7-day free trial available!',
                                meta_keywords: 'darija pro, moroccan arabic premium, darija membership'
                              }
                            ];
                            
                            // Save each meta tag
                            for (const page of pages) {
                              const formData = new FormData();
                              formData.append('page_path', page.page_path);
                              formData.append('page_title', page.page_title);
                              formData.append('meta_title', page.meta_title);
                              formData.append('meta_description', page.meta_description);
                              formData.append('meta_keywords', page.meta_keywords);
                              formData.append('og_title', page.meta_title);
                              formData.append('og_description', page.meta_description);
                              formData.append('og_image', '');
                              formData.append('canonical_url', `${window.location.origin}${page.page_path}`);
                              formData.append('robots', 'index, follow');
                              
                              try {
                                await fetch(`${backendUrl}/api/admin/seo/meta-tags`, {
                                  method: 'POST',
                                  headers: { 'Authorization': `Bearer ${token}` },
                                  body: formData
                                });
                              } catch (e) {
                                console.log('Meta tag might exist, continuing...');
                              }
                            }
                            await new Promise(resolve => setTimeout(resolve, 1500));
                            
                            // STEP 3: Update Image Settings
                            showNotification('🖼️ Enabling automatic image ALT text...', 'success');
                            setImageSettings({
                              add_missing_alt: true,
                              alt_format: '%title% - Darija Pro',
                              add_missing_title: true,
                              title_format: '%title%'
                            });
                            await new Promise(resolve => setTimeout(resolve, 1000));
                            
                            // STEP 4: Enable Security
                            showNotification('🔒 Activating all security features...', 'success');
                            setSecuritySettings({
                              xframe_options: true,
                              xss_protection: true,
                              hsts_enabled: true,
                              directory_listing_prevention: true,
                              protected_directories: [
                                { path: '/admin/', action: 'redirect_home', status: 'active' },
                                { path: '/api/', action: 'access_denied', status: 'active' },
                                { path: '/backend/', action: 'blocked', status: 'active' }
                              ]
                            });
                            await new Promise(resolve => setTimeout(resolve, 1000));
                            
                            // STEP 5: Update Sitemap
                            showNotification('🗺️ Configuring sitemap...', 'success');
                            setSitemapSettings({
                              sitemap_enabled: true,
                              sitemap_priority_home: 1.0,
                              sitemap_priority_pages: 0.8,
                              sitemap_changefreq: 'daily'
                            });
                            await new Promise(resolve => setTimeout(resolve, 1000));
                            
                            // STEP 5.5: Generate BEST Keywords and Update General Settings
                            showNotification('🔑 AI generating best keywords and tagline...', 'success');
                            const bestKeywords = [
                              'learn darija online free',
                              'moroccan arabic lessons for beginners',
                              'darija translation app',
                              'speak moroccan dialect fluently',
                              'arabic language learning games',
                              'moroccan arabic pronunciation guide',
                              'darija conversation practice online',
                              'learn moroccan arabic fast',
                              'darija vocabulary builder',
                              'moroccan arabic course',
                              'arabic dialect morocco',
                              'darija language app',
                              'moroccan arabic phrases',
                              'learn darija from english',
                              'moroccan arabic grammar lessons'
                            ];
                            setWebsiteKeywords(bestKeywords);
                            
                            setGeneralSettings(prev => ({
                              ...prev,
                              site_title: 'Darija Pro - Master Moroccan Arabic Online Fast & Easy',
                              tagline: 'Learn Moroccan Darija through interactive games, instant translation, and daily lessons. Join 10,000+ learners speaking fluently!',
                              primary_keyword: 'learn moroccan arabic online',
                              secondary_keywords: 'darija lessons, moroccan dialect course, arabic games, learn darija fast, moroccan arabic for beginners'
                            }));
                            await new Promise(resolve => setTimeout(resolve, 1000));
                            
                            // STEP 6: Save ALL settings to backend
                            showNotification('💾 Saving all optimizations to database...', 'success');
                            await handleSaveSeoSettings();
                            await new Promise(resolve => setTimeout(resolve, 1000));
                            
                            // STEP 7: Reload and recalculate
                            showNotification('♻️ Refreshing SEO data...', 'success');
                            await loadSEOData();
                            calculateSEOScore();
                            
                            setAiGenerating(false);
                            showNotification('🎉 AI Complete! Robots.txt filled, Meta tags created, Security enabled, Everything optimized!', 'success');
                            
                          } catch (error) {
                            setAiGenerating(false);
                            showNotification('❌ AI optimization failed: ' + error.message, 'error');
                            console.error('AI optimization error:', error);
                          }
                        }}
                        disabled={aiGenerating}
                        className={`w-full px-6 py-4 rounded-lg font-bold text-lg transition-all ${
                          aiGenerating
                            ? 'bg-gray-500 cursor-not-allowed'
                            : 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white'
                        }`}
                      >
                        {aiGenerating ? (
                          <span className="flex items-center justify-center gap-2">
                            <span className="animate-spin">⚙️</span> AI is Scanning & Fixing... Please Wait
                          </span>
                        ) : (
                          <span className="flex items-center justify-center gap-2">
                            ✨ Run AI Website Optimizer (Fix Everything)
                          </span>
                        )}
                      </button>
                    </div>
                    
                    {/* AI Keyword Research */}
                    <div className={`p-6 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}>
                      <h3 className={`text-lg font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        🔍 AI Keyword Research
                      </h3>
                      
                      <p className={`mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        Get AI-powered keyword suggestions for your content
                      </p>
                      
                      <button
                        onClick={() => {
                          const suggestedKeywords = [
                            'learn darija online free',
                            'moroccan arabic for beginners',
                            'darija conversation practice',
                            'moroccan dialect phrases',
                            'arabic darija vocabulary',
                            'learn moroccan arabic app',
                            'darija language course',
                            'moroccan arabic pronunciation',
                            'speak darija fluently',
                            'moroccan arabic grammar'
                          ];
                          showNotification(`🔍 AI Generated ${suggestedKeywords.length} keyword ideas! Check Keywords tab.`, 'success');
                          setWebsiteKeywords([...new Set([...websiteKeywords, ...suggestedKeywords])]);
                        }}
                        className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                      >
                        🔍 Generate Keyword Ideas
                      </button>
                    </div>
                    
                    {/* AI Content Suggestions */}
                    <div className={`p-6 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}>
                      <h3 className={`text-lg font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        📝 AI Content Suggestions
                      </h3>
                      
                      <p className={`mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        Get sophisticated AI suggestions for related keywords, questions & links
                      </p>
                      
                      <button
                        onClick={() => {
                          const contentIdeas = [
                            '📝 "Top 10 Moroccan Darija Phrases for Tourists"',
                            '📝 "How to Learn Darija in 30 Days"',
                            '📝 "Darija vs Modern Standard Arabic: Key Differences"',
                            '📝 "Common Darija Mistakes and How to Avoid Them"',
                            '📝 "Moroccan Culture Through Language Learning"'
                          ];
                          alert('💡 AI Content Suggestions:\n\n' + contentIdeas.join('\n\n') + '\n\nCreate blog posts or pages with these titles!');
                        }}
                        className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
                      >
                        💡 Get Content Ideas
                      </button>
                    </div>
                    
                    {/* Quick Actions */}
                    <div className={`p-6 rounded-lg ${isDarkMode ? 'bg-gradient-to-br from-green-900 to-teal-900' : 'bg-gradient-to-br from-green-50 to-teal-50'}`}>
                      <h3 className={`text-xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        ⚡ Quick Actions
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <button className={`p-3 rounded-lg font-medium text-left ${
                          isDarkMode ? 'bg-gray-800 hover:bg-gray-700 text-white' : 'bg-white hover:bg-gray-50 text-gray-900'
                        }`}>
                          🎯 Optimize All Meta Tags
                        </button>
                        <button className={`p-3 rounded-lg font-medium text-left ${
                          isDarkMode ? 'bg-gray-800 hover:bg-gray-700 text-white' : 'bg-white hover:bg-gray-50 text-gray-900'
                        }`}>
                          🖼️ Generate Image ALT Text
                        </button>
                        <button className={`p-3 rounded-lg font-medium text-left ${
                          isDarkMode ? 'bg-gray-800 hover:bg-gray-700 text-white' : 'bg-white hover:bg-gray-50 text-gray-900'
                        }`}>
                          🔗 Fix Internal Links
                        </button>
                        <button className={`p-3 rounded-lg font-medium text-left ${
                          isDarkMode ? 'bg-gray-800 hover:bg-gray-700 text-white' : 'bg-white hover:bg-gray-50 text-gray-900'
                        }`}>
                          📊 Full SEO Audit
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === '404-page' && (
                <div>
                  <div className="flex justify-between items-center mb-4">
                    <div>
                      <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        404 Page Editor
                      </h2>
                      <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        Edit all text, buttons, and links on the 404 error page. Changes sync automatically.
                      </p>
                    </div>
                    <button
                      onClick={handleSave404Page}
                      className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium"
                    >
                      Save Changes
                    </button>
                  </div>

                  <div className="space-y-6">
                    {/* Main Content */}
                    <div className={`p-6 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}>
                      <h3 className={`text-lg font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        Text Content
                      </h3>
                      <div className="space-y-4">
                        <div>
                          <label className={`block mb-1 text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            Speech Bubble Text
                          </label>
                          <input
                            type="text"
                            value={page404Settings.speech_bubble}
                            onChange={(e) => setPage404Settings(p => ({ ...p, speech_bubble: e.target.value }))}
                            className={`w-full p-3 rounded-lg border-2 ${isDarkMode ? 'bg-gray-800 text-white border-gray-600' : 'bg-white text-gray-900 border-gray-300'}`}
                          />
                        </div>
                        <div>
                          <label className={`block mb-1 text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            Heading
                          </label>
                          <input
                            type="text"
                            value={page404Settings.heading}
                            onChange={(e) => setPage404Settings(p => ({ ...p, heading: e.target.value }))}
                            className={`w-full p-3 rounded-lg border-2 ${isDarkMode ? 'bg-gray-800 text-white border-gray-600' : 'bg-white text-gray-900 border-gray-300'}`}
                          />
                        </div>
                        <div>
                          <label className={`block mb-1 text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            Description
                          </label>
                          <input
                            type="text"
                            value={page404Settings.description}
                            onChange={(e) => setPage404Settings(p => ({ ...p, description: e.target.value }))}
                            className={`w-full p-3 rounded-lg border-2 ${isDarkMode ? 'bg-gray-800 text-white border-gray-600' : 'bg-white text-gray-900 border-gray-300'}`}
                          />
                        </div>
                        <div>
                          <label className={`block mb-1 text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            Footer / Copyright Text
                          </label>
                          <input
                            type="text"
                            value={page404Settings.footer_text}
                            onChange={(e) => setPage404Settings(p => ({ ...p, footer_text: e.target.value }))}
                            placeholder="e.g. DarijaPro 2026"
                            className={`w-full p-3 rounded-lg border-2 ${isDarkMode ? 'bg-gray-800 text-white border-gray-600' : 'bg-white text-gray-900 border-gray-300'}`}
                          />
                          <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                            Shown at the bottom of the 404 page
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Custom Image */}
                    <div className={`p-6 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}>
                      <h3 className={`text-lg font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        Custom Image
                      </h3>
                      <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        Leave empty to use the default dog SVG. Upload an image or paste an external URL (Dropbox, etc.).
                      </p>

                      {/* Upload button */}
                      <div className="flex gap-3 mb-3">
                        <label className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium cursor-pointer text-sm">
                          Upload Image
                          <input
                            type="file"
                            accept="image/png,image/jpeg,image/gif,image/webp"
                            className="hidden"
                            onChange={async (e) => {
                              const file = e.target.files[0];
                              if (!file) return;
                              try {
                                const token = getAuthToken();
                                const backendUrl = process.env.REACT_APP_BACKEND_URL;
                                const formData = new FormData();
                                formData.append('file', file);
                                formData.append('image_type', '404_page');
                                const res = await fetch(`${backendUrl}/api/admin/seo/upload-image`, {
                                  method: 'POST',
                                  headers: { 'Authorization': `Bearer ${token}` },
                                  body: formData
                                });
                                if (res.ok) {
                                  const data = await res.json();
                                  const fullUrl = `${backendUrl}${data.url}`;
                                  setPage404Settings(p => ({ ...p, image_url: fullUrl }));
                                  showNotification('Image uploaded successfully!', 'success');
                                } else {
                                  showNotification('Failed to upload image', 'error');
                                }
                              } catch (err) {
                                showNotification('Upload error: ' + err.message, 'error');
                              }
                              e.target.value = '';
                            }}
                          />
                        </label>
                        <span className={`self-center text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>or</span>
                      </div>

                      {/* URL input */}
                      <input
                        type="text"
                        value={page404Settings.image_url}
                        onChange={(e) => setPage404Settings(p => ({ ...p, image_url: e.target.value }))}
                        placeholder="Paste URL (Dropbox, external link, etc.)"
                        className={`w-full p-3 rounded-lg border-2 ${isDarkMode ? 'bg-gray-800 text-white border-gray-600' : 'bg-white text-gray-900 border-gray-300'}`}
                      />
                      <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        Supports: PNG, JPG, GIF, WEBP (max 5MB) or any external image URL
                      </p>

                      {/* Preview */}
                      {page404Settings.image_url && (
                        <div className={`mt-4 p-4 rounded-lg flex items-center gap-4 ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
                          <img src={page404Settings.image_url} alt="Preview" className="w-24 h-24 object-contain rounded-lg border" />
                          <div>
                            <p className={`text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Current image</p>
                            <button
                              onClick={() => setPage404Settings(p => ({ ...p, image_url: '' }))}
                              className="px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700"
                            >
                              Remove Image
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Action Buttons */}
                    <div className={`p-6 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}>
                      <h3 className={`text-lg font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        Action Buttons
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-3">
                          <h4 className={`font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Button 1 (Primary)</h4>
                          <input
                            type="text"
                            value={page404Settings.button1_icon}
                            onChange={(e) => setPage404Settings(p => ({ ...p, button1_icon: e.target.value }))}
                            placeholder="Icon (emoji)"
                            className={`w-full p-2 rounded-lg border-2 ${isDarkMode ? 'bg-gray-800 text-white border-gray-600' : 'bg-white text-gray-900 border-gray-300'}`}
                          />
                          <input
                            type="text"
                            value={page404Settings.button1_label}
                            onChange={(e) => setPage404Settings(p => ({ ...p, button1_label: e.target.value }))}
                            placeholder="Label"
                            className={`w-full p-2 rounded-lg border-2 ${isDarkMode ? 'bg-gray-800 text-white border-gray-600' : 'bg-white text-gray-900 border-gray-300'}`}
                          />
                          <input
                            type="text"
                            value={page404Settings.button1_path}
                            onChange={(e) => setPage404Settings(p => ({ ...p, button1_path: e.target.value }))}
                            placeholder="Path (e.g. /)"
                            className={`w-full p-2 rounded-lg border-2 ${isDarkMode ? 'bg-gray-800 text-white border-gray-600' : 'bg-white text-gray-900 border-gray-300'}`}
                          />
                        </div>
                        <div className="space-y-3">
                          <h4 className={`font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Button 2 (Secondary)</h4>
                          <input
                            type="text"
                            value={page404Settings.button2_icon}
                            onChange={(e) => setPage404Settings(p => ({ ...p, button2_icon: e.target.value }))}
                            placeholder="Icon (emoji)"
                            className={`w-full p-2 rounded-lg border-2 ${isDarkMode ? 'bg-gray-800 text-white border-gray-600' : 'bg-white text-gray-900 border-gray-300'}`}
                          />
                          <input
                            type="text"
                            value={page404Settings.button2_label}
                            onChange={(e) => setPage404Settings(p => ({ ...p, button2_label: e.target.value }))}
                            placeholder="Label"
                            className={`w-full p-2 rounded-lg border-2 ${isDarkMode ? 'bg-gray-800 text-white border-gray-600' : 'bg-white text-gray-900 border-gray-300'}`}
                          />
                          <input
                            type="text"
                            value={page404Settings.button2_path}
                            onChange={(e) => setPage404Settings(p => ({ ...p, button2_path: e.target.value }))}
                            placeholder="Path (e.g. /translation)"
                            className={`w-full p-2 rounded-lg border-2 ${isDarkMode ? 'bg-gray-800 text-white border-gray-600' : 'bg-white text-gray-900 border-gray-300'}`}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Preview Link */}
                    <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-blue-900/30' : 'bg-blue-50'}`}>
                      <p className={`text-sm ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                        Preview your 404 page: <a href="/404" target="_blank" rel="noopener noreferrer" className="underline font-medium">Open 404 Page</a>
                        <span className="ml-2">(Save first, then refresh the preview page to see changes)</span>
                      </p>
                    </div>

                    {/* Save button at bottom */}
                    <button
                      onClick={handleSave404Page}
                      className="w-full px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-bold text-lg"
                    >
                      Save 404 Page Settings
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
          </div>
        </div>

        {/* Meta Tag Modal */}
        {showMetaModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-xl shadow-2xl ${
              isDarkMode ? 'bg-gray-800' : 'bg-white'
            }`}>
              <div className={`sticky top-0 z-10 p-6 border-b ${
                isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
              }`}>
                <div className="flex justify-between items-center">
                  <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {editingMeta ? 'Edit' : 'Add'} Meta Tags
                  </h2>
                  <button
                    onClick={() => {
                      setShowMetaModal(false);
                      setEditingMeta(null);
                    }}
                    className={`text-2xl ${isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
                  >
                    ✕
                  </button>
                </div>
              </div>
              
              <div className="p-6 space-y-6">
                {/* ─── BASIC INFO ──────────────────────────────────── */}
                <div className={`pb-1 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                  <h3 className={`text-sm font-bold uppercase tracking-wide ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`}>
                    📄 Basic Info
                  </h3>
                </div>

                <div>
                  <label className={`block mb-2 font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    Page Path <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={newMeta.page_path}
                    onChange={(e) => setNewMeta({...newMeta, page_path: e.target.value})}
                    className={`w-full p-3 rounded-lg font-mono text-sm ${isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-900 border-gray-300'} border-2`}
                    placeholder="/about"
                  />
                  <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    Must match the actual frontend route (case-sensitive). Examples: <code>/translation</code>, <code>/dictionary</code>, <code>/learn</code>
                  </p>
                </div>

                <div>
                  <label className={`block mb-2 font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    Page Title <span className={`text-xs font-normal ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>(internal label, not shown to users)</span>
                  </label>
                  <input
                    type="text"
                    value={newMeta.page_title}
                    onChange={(e) => setNewMeta({...newMeta, page_title: e.target.value})}
                    className={`w-full p-3 rounded-lg ${isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-900 border-gray-300'} border-2`}
                    placeholder="Used in admin list to identify this page"
                  />
                </div>

                {/* ─── SEARCH ENGINES (TITLE + DESCRIPTION + KEYWORDS) ── */}
                <div className={`pb-1 border-b mt-6 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                  <h3 className={`text-sm font-bold uppercase tracking-wide ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`}>
                    🔍 Search Engines (Google, Bing)
                  </h3>
                </div>

                <div>
                  <label className={`block mb-2 font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    Meta Title <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={newMeta.meta_title}
                    onChange={(e) => setNewMeta({...newMeta, meta_title: e.target.value})}
                    className={`w-full p-3 rounded-lg ${isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-900 border-gray-300'} border-2`}
                    placeholder="What appears as the blue link in Google results"
                  />
                  <p className={`text-xs mt-1 ${
                    newMeta.meta_title.length > 60 ? 'text-red-500 font-medium' : (isDarkMode ? 'text-gray-400' : 'text-gray-600')
                  }`}>
                    {newMeta.meta_title.length}/60 characters {newMeta.meta_title.length > 60 ? '— Google may truncate' : ''}
                  </p>
                </div>

                <div>
                  <label className={`block mb-2 font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    Meta Description <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={newMeta.meta_description}
                    onChange={(e) => setNewMeta({...newMeta, meta_description: e.target.value})}
                    rows={3}
                    className={`w-full p-3 rounded-lg ${isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-900 border-gray-300'} border-2`}
                    placeholder="The 1-2 sentence summary that appears under the title in Google"
                  />
                  <p className={`text-xs mt-1 ${
                    newMeta.meta_description.length > 160 ? 'text-red-500 font-medium' : (isDarkMode ? 'text-gray-400' : 'text-gray-600')
                  }`}>
                    {newMeta.meta_description.length}/160 characters {newMeta.meta_description.length > 160 ? '— Google may truncate' : ''}
                  </p>
                </div>

                <div>
                  <label className={`block mb-2 font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    Keywords <span className={`text-xs font-normal ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>(comma-separated; Bing/Yandex use these, Google ignores)</span>
                  </label>
                  <input
                    type="text"
                    value={newMeta.meta_keywords}
                    onChange={(e) => setNewMeta({...newMeta, meta_keywords: e.target.value})}
                    className={`w-full p-3 rounded-lg ${isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-900 border-gray-300'} border-2`}
                    placeholder="darija, moroccan arabic, learn language"
                  />
                </div>

                <div>
                  <label className={`block mb-2 font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    Canonical URL
                  </label>
                  <input
                    type="url"
                    value={newMeta.canonical_url}
                    onChange={(e) => setNewMeta({...newMeta, canonical_url: e.target.value})}
                    className={`w-full p-3 rounded-lg ${isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-900 border-gray-300'} border-2`}
                    placeholder="https://darijapro.com/about"
                  />
                  <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    Tells Google this is the official URL for this content. Leave blank to default to the current page URL.
                  </p>
                </div>

                <div>
                  <label className={`block mb-2 font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    Robots
                  </label>
                  <select
                    value={newMeta.robots}
                    onChange={(e) => setNewMeta({...newMeta, robots: e.target.value})}
                    className={`w-full p-3 rounded-lg ${isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-900 border-gray-300'} border-2`}
                  >
                    <option value="index, follow">Index, Follow (default — page in search results)</option>
                    <option value="noindex, follow">No Index, Follow (hide from search but follow links)</option>
                    <option value="index, nofollow">Index, No Follow</option>
                    <option value="noindex, nofollow">No Index, No Follow (hidden from search)</option>
                  </select>
                </div>

                {/* ─── OPEN GRAPH (FACEBOOK / LINKEDIN) ──────────────── */}
                <div className={`pb-1 border-b mt-6 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                  <h3 className={`text-sm font-bold uppercase tracking-wide ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`}>
                    📘 Open Graph (Facebook, LinkedIn, WhatsApp)
                  </h3>
                  <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    How the page looks when shared on social networks. Leave blank to fall back to Meta Title / Description.
                  </p>
                </div>

                <div>
                  <label className={`block mb-2 font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    OG Image URL <span className={`text-xs font-normal ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>(1200×630 recommended)</span>
                  </label>
                  <input
                    type="url"
                    value={newMeta.og_image}
                    onChange={(e) => setNewMeta({...newMeta, og_image: e.target.value})}
                    className={`w-full p-3 rounded-lg ${isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-900 border-gray-300'} border-2`}
                    placeholder="https://darijapro.com/og-about.png"
                  />
                  {newMeta.og_image && (
                    <div className="mt-2 flex items-center gap-3">
                      <img src={newMeta.og_image} alt="OG preview" className="w-32 h-16 object-cover rounded border" onError={(e) => { e.target.style.display = 'none'; }} />
                      <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>preview</p>
                    </div>
                  )}
                </div>

                <div>
                  <label className={`block mb-2 font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    OG Title
                  </label>
                  <input
                    type="text"
                    value={newMeta.og_title || ''}
                    onChange={(e) => setNewMeta({...newMeta, og_title: e.target.value})}
                    className={`w-full p-3 rounded-lg ${isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-900 border-gray-300'} border-2`}
                    placeholder={newMeta.meta_title || 'Falls back to Meta Title if blank'}
                  />
                </div>

                <div>
                  <label className={`block mb-2 font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    OG Description
                  </label>
                  <textarea
                    value={newMeta.og_description || ''}
                    onChange={(e) => setNewMeta({...newMeta, og_description: e.target.value})}
                    rows={2}
                    className={`w-full p-3 rounded-lg ${isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-900 border-gray-300'} border-2`}
                    placeholder={newMeta.meta_description || 'Falls back to Meta Description if blank'}
                  />
                </div>

                {/* ─── TWITTER / X CARD ──────────────────────────────── */}
                <div className={`pb-1 border-b mt-6 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                  <h3 className={`text-sm font-bold uppercase tracking-wide ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`}>
                    🐦 Twitter / X Card
                  </h3>
                  <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Card layout when shared on X/Twitter. Leave blank to fall back to Open Graph.
                  </p>
                </div>

                <div>
                  <label className={`block mb-2 font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    Card Type
                  </label>
                  <select
                    value={newMeta.twitter_card || 'summary_large_image'}
                    onChange={(e) => setNewMeta({...newMeta, twitter_card: e.target.value})}
                    className={`w-full p-3 rounded-lg ${isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-900 border-gray-300'} border-2`}
                  >
                    <option value="summary_large_image">Summary Large Image (recommended)</option>
                    <option value="summary">Summary (small image)</option>
                  </select>
                </div>

                <div>
                  <label className={`block mb-2 font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    Twitter Image URL
                  </label>
                  <input
                    type="url"
                    value={newMeta.twitter_image || ''}
                    onChange={(e) => setNewMeta({...newMeta, twitter_image: e.target.value})}
                    className={`w-full p-3 rounded-lg ${isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-900 border-gray-300'} border-2`}
                    placeholder={newMeta.og_image || 'Falls back to OG Image if blank'}
                  />
                </div>

                <div>
                  <label className={`block mb-2 font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    Twitter Title
                  </label>
                  <input
                    type="text"
                    value={newMeta.twitter_title || ''}
                    onChange={(e) => setNewMeta({...newMeta, twitter_title: e.target.value})}
                    className={`w-full p-3 rounded-lg ${isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-900 border-gray-300'} border-2`}
                    placeholder={newMeta.og_title || newMeta.meta_title || 'Falls back to OG/Meta Title'}
                  />
                </div>

                <div>
                  <label className={`block mb-2 font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    Twitter Description
                  </label>
                  <textarea
                    value={newMeta.twitter_description || ''}
                    onChange={(e) => setNewMeta({...newMeta, twitter_description: e.target.value})}
                    rows={2}
                    className={`w-full p-3 rounded-lg ${isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-900 border-gray-300'} border-2`}
                    placeholder={newMeta.og_description || newMeta.meta_description || 'Falls back to OG/Meta Description'}
                  />
                </div>

                {/* ─── STRUCTURED DATA (JSON-LD) ───────────────────── */}
                <div className={`pb-1 border-b mt-6 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                  <h3 className={`text-sm font-bold uppercase tracking-wide ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`}>
                    📐 Structured Data (JSON-LD)
                  </h3>
                  <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Schema.org markup for Google rich results. Optional. Validate at <code>schema.org/validator</code> before saving.
                  </p>
                </div>

                <div>
                  <label className={`block mb-2 font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    JSON-LD Schema
                  </label>
                  <textarea
                    value={newMeta.schema_markup || ''}
                    onChange={(e) => setNewMeta({...newMeta, schema_markup: e.target.value})}
                    rows={8}
                    className={`w-full p-3 rounded-lg font-mono text-xs ${isDarkMode ? 'bg-gray-900 text-green-300 border-gray-600' : 'bg-gray-50 text-gray-900 border-gray-300'} border-2`}
                    placeholder={`{\n  "@context": "https://schema.org",\n  "@type": "WebPage",\n  "name": "Your page name",\n  "description": "..."\n}`}
                  />
                </div>
              </div>
              
              <div className={`sticky bottom-0 p-6 border-t ${
                isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
              }`}>
                <div className="flex gap-3 justify-end">
                  <button
                    onClick={() => {
                      setShowMetaModal(false);
                      setEditingMeta(null);
                    }}
                    className={`px-6 py-2 rounded-lg font-medium ${
                      isDarkMode 
                        ? 'bg-gray-700 text-white hover:bg-gray-600' 
                        : 'bg-gray-200 text-gray-900 hover:bg-gray-300'
                    }`}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveMetaTag}
                    className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium"
                  >
                    💾 Save Meta Tags
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Redirect Modal */}
        {showRedirectModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`w-full max-w-2xl rounded-xl shadow-2xl ${
              isDarkMode ? 'bg-gray-800' : 'bg-white'
            }`}>
              <div className="p-6 border-b border-gray-700">
                <div className="flex justify-between items-center">
                  <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    Add 301 Redirect
                  </h2>
                  <button
                    onClick={() => setShowRedirectModal(false)}
                    className={`text-2xl ${isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
                  >
                    ✕
                  </button>
                </div>
              </div>
              
              <div className="p-6 space-y-4">
                <div>
                  <label className={`block mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    From URL (old path)
                  </label>
                  <input
                    type="text"
                    value={newRedirect.from_url}
                    onChange={(e) => setNewRedirect({...newRedirect, from_url: e.target.value})}
                    placeholder="/old-page"
                    className={`w-full p-3 rounded-lg ${
                      isDarkMode 
                        ? 'bg-gray-700 text-white border-gray-600' 
                        : 'bg-white text-gray-900 border-gray-300'
                    } border-2`}
                  />
                </div>
                
                <div>
                  <label className={`block mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    To URL (new path)
                  </label>
                  <input
                    type="text"
                    value={newRedirect.to_url}
                    onChange={(e) => setNewRedirect({...newRedirect, to_url: e.target.value})}
                    placeholder="/new-page"
                    className={`w-full p-3 rounded-lg ${
                      isDarkMode 
                        ? 'bg-gray-700 text-white border-gray-600' 
                        : 'bg-white text-gray-900 border-gray-300'
                    } border-2`}
                  />
                </div>
                
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={newRedirect.enabled}
                    onChange={(e) => setNewRedirect({...newRedirect, enabled: e.target.checked})}
                    className="w-5 h-5"
                  />
                  <label className={isDarkMode ? 'text-white' : 'text-gray-900'}>
                    Enable redirect immediately
                  </label>
                </div>
              </div>
              
              <div className="p-6 border-t border-gray-700">
                <div className="flex gap-3 justify-end">
                  <button
                    onClick={() => setShowRedirectModal(false)}
                    className={`px-6 py-2 rounded-lg font-medium ${
                      isDarkMode 
                        ? 'bg-gray-700 text-white hover:bg-gray-600' 
                        : 'bg-gray-200 text-gray-900 hover:bg-gray-300'
                    }`}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveRedirect}
                    className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium"
                  >
                    💾 Create Redirect
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SEOManagementPanel;
